/*
 * Launch Welcome gift - cart side.
 *
 * The rules that decide whether a gift may sit in the cart are pure functions
 * at the top of this file, deliberately separated from anything that touches
 * the DOM or the network, so they can be run and asserted outside a browser.
 * Everything below `start()` is wiring.
 *
 * Shopify is always the pricing authority. The two native roads use automatic
 * discounts; the OFF-by-default mixed canary applies a short-lived,
 * customer-bound code issued by the signed Worker. This file never paints a
 * price. That is why `decideGift` treats a gift line that still carries a price
 * as something to remove: carrying it into checkout would charge a customer.
 *
 * Two paths lead to the same entitlement and the same size picker: a cart that
 * crosses the threshold now, or a year's accrual that already has. The second
 * is why an empty cart can still be entitled.
 */

/**
 * Reduce a cart payload to the facts the rules need.
 *
 * The qualifying subtotal deliberately excludes the gift's own line. Once the
 * discount lands the gift is worth nothing anyway, but a gift that was NOT
 * discounted must never be what pushes a cart over the threshold.
 *
 * `final_line_price` is the line after discounts. Shipping never appears in
 * cart items, so it is excluded by construction rather than by subtraction.
 *
 * One discount is added back: the floor's own code. It is the benefit the
 * threshold earned, allocated across the lines by Shopify, and a cart of
 * exactly the threshold would otherwise fall under it the moment the code
 * lands, turning the code into a stray. Shopify judges the code's minimum
 * on the subtotal before it, and the measurement here agrees.
 */
function floorAllocationMinor(item) {
  var allocations = Array.isArray(item.line_level_discount_allocations)
    ? item.line_level_discount_allocations : [];
  var sum = 0;
  for (var i = 0; i < allocations.length; i += 1) {
    var allocation = allocations[i];
    var application = allocation && allocation.discount_application;
    if (!application || application.type !== 'discount_code') continue;
    if (!FLOOR_CODE_RE.test(normalizedCode(application.title))) continue;
    if (safeMinor(allocation.amount)) sum += allocation.amount;
  }
  return sum;
}

function measureCart(cart, variantIds) {
  var ids = variantIds || [];
  var out = { qualifyingMinor: 0, giftLines: 0, giftQuantity: 0, giftCharged: false, giftVariant: 0 };
  var items = (cart && cart.items) || [];
  for (var i = 0; i < items.length; i += 1) {
    var item = items[i];
    var variantId = item.variant_id || item.id;
    if (ids.indexOf(variantId) !== -1) {
      out.giftLines += 1;
      out.giftQuantity += item.quantity;
      out.giftVariant = variantId;
      if (item.final_line_price > 0) out.giftCharged = true;
    } else if (String(item.product_type || '') !== 'free_gift') {
      // The qualifying collection excludes free_gift merchandise; the cart
      // measurement follows the same rule so both sides agree on the sum.
      out.qualifyingMinor += item.final_line_price + floorAllocationMinor(item);
    }
  }
  return out;
}

/**
 * The single place that decides what should happen to the cart.
 *
 * Returns one of:
 *   remove-gift   the gift may not stay; reason says why
 *   fix-quantity  one gift, wrong quantity
 *   ask-member    entitled, nothing chosen yet
 *   ask-join      logged in, over the line, not a member yet
 *   ask-account   over the line, not authenticated
 *   none          the cart is already in a legal state
 *
 * Removals are ordered before everything else on purpose: a cart that is
 * wrong should be corrected before anyone is asked anything. Within the
 * removals `charged` outranks the rest - whatever else is true, a customer
 * must not reach checkout paying for a gift.
 */
function decideGift(measured, options) {
  var thresholdMinor = options.thresholdMinor || 0;
  var isMember = options.isMember === true;
  var isLoggedIn = options.isLoggedIn === true;
  var hasRedeemed = options.hasRedeemed === true;
  var spendMinor = options.spendMinor || 0;

  var campaignOn = thresholdMinor > 0;
  var cartQualifies = campaignOn && measured.qualifyingMinor >= thresholdMinor;
  // Accrual only means anything for a member: a guest has no ledger, and a
  // figure left on a customer who is no longer in the club must not become an
  // entitlement of its own.
  var spendQualifies = campaignOn && isMember && spendMinor >= thresholdMinor;
  var eligible = cartQualifies || spendQualifies;

  if (measured.giftLines > 0) {
    if (measured.giftCharged) return { action: 'remove-gift', reason: 'charged' };
    if (!isMember) return { action: 'remove-gift', reason: 'not-a-member' };
    if (hasRedeemed) return { action: 'remove-gift', reason: 'already-redeemed' };
    if (!eligible) return { action: 'remove-gift', reason: 'below-threshold' };
    if (measured.giftLines > 1) return { action: 'remove-gift', reason: 'duplicate' };
    if (measured.giftQuantity !== 1) {
      return { action: 'fix-quantity', reason: 'quantity', variant: measured.giftVariant };
    }
    return { action: 'none', reason: 'settled' };
  }

  /*
   * Everything above answers for a line that is already in her cart: it stays
   * hers, and a charged one still comes off, whatever the shelf holds. Only
   * the invitation depends on stock. An offer with every size struck through
   * is a promise the campaign cannot keep, so it is not made. Unknown is not
   * empty - the flag has to say false before anything goes quiet.
   */
  var shelfEmpty = options.giftAvailable === false;
  // With every size gone the campaign has one more thing to give: the floor,
  // an order-level percentage the Worker issues in the gift's place. It is
  // offered only when the section says it may be; otherwise an empty shelf
  // still ends the offer here.
  var floor = shelfEmpty && options.floorEnabled === true;
  if (shelfEmpty && !floor) return { action: 'none', reason: 'out-of-stock' };

  // Once in a lifetime. A second gift is not offered, however large the cart.
  if (hasRedeemed) return { action: 'none', reason: 'already-redeemed' };
  if (!eligible) return { action: 'none', reason: 'below-threshold' };
  if (isMember) {
    if (floor) return { action: 'ask-member', reason: cartQualifies ? 'entitled-floor' : 'entitled-floor-by-spend' };
    return { action: 'ask-member', reason: cartQualifies ? 'entitled' : 'entitled-by-spend' };
  }
  return isLoggedIn
    ? { action: 'ask-join', reason: 'logged-in-not-member' }
    : { action: 'ask-account', reason: 'logged-out' };
}

/*
 * The mixed-threshold bridge is deliberately an overlay, not a third branch in
 * decideGift. Its local result is only a hint that it is worth asking the
 * signed Worker for a grant; it never authorizes a price or keeps a charged
 * line alive.
 */
// Public rollout is intentionally not a runtime mode. Widening the bridge
// must require an explicit, reviewed code change rather than a variable flip.
var MIXED_MODES = ['off', 'canary'];

function mixedModeOf(raw) {
  var mode = String(raw || 'off').trim().toLowerCase();
  return MIXED_MODES.indexOf(mode) === -1 ? 'off' : mode;
}

function mixedMinorValue(raw, missingAsZero) {
  if (missingAsZero && (raw === undefined || raw === null || raw === '')) return 0;
  return Number.isSafeInteger(raw) && raw >= 0 ? raw : null;
}

/*
 * The mixed lane in one paragraph.
 *
 * Roni is a member with stored spend below the tier threshold and a cart
 * below it too, whose sum clears it. Neither native lane can see that, so a
 * signed Worker grant issues her a personal BXGY code for the gap and Shopify
 * makes the gift free in the same order that lifts her a tier.
 *
 * Success has exactly one definition: after a fresh cart read there is one
 * marked line of the current campaign, quantity 1, `final_line_price` the
 * integer 0. Which discount produced that zero is Shopify's business.
 *
 * A marked line that is not free is never presented as a gift. It is either
 * repaired or removed, and a removal that cannot be confirmed locks checkout
 * rather than letting a charged "gift" reach payment.
 */

var KCG_CODE_RE = /^KCG-[0-9A-F]{24}$/i;
var GRANT_OUTCOMES = ['created', 'reused', 'recovered'];

function safeMinor(value) {
  return Number.isSafeInteger(value) && value >= 0;
}

function normalizedCode(raw) {
  return typeof raw === 'string' ? raw.trim() : '';
}

function cartExplicitCodes(cart) {
  var entries = (cart && cart.discount_codes) || [];
  var out = [];
  if (!Array.isArray(entries)) return out;
  for (var i = 0; i < entries.length; i += 1) {
    var entry = entries[i];
    if (!entry || typeof entry !== 'object') continue;
    var code = normalizedCode(entry.code);
    if (code) out.push(code);
  }
  return out;
}

/*
 * A discount list we cannot read is a list we must not rewrite: sending a
 * partial list would silently drop a code the shopper entered.
 */
function cartCodesReadable(cart) {
  if (!cart) return false;
  var entries = cart.discount_codes;
  if (entries == null) return true;
  if (!Array.isArray(entries)) return false;
  for (var i = 0; i < entries.length; i += 1) {
    var entry = entries[i];
    if (!entry || typeof entry !== 'object') return false;
    if (normalizedCode(entry.code) === '') return false;
  }
  return true;
}

/*
 * Shopify's `cart/update.js` replaces the whole discount list, so the only
 * safe write is every code we just observed plus ours. Sending an empty
 * discount value would delete every shopper code, so it is never emitted.
 */
function mergeDiscountCodes(existing, code) {
  var list = [];
  var seen = {};
  var source = Array.isArray(existing) ? existing : [];
  for (var i = 0; i < source.length; i += 1) {
    var value = normalizedCode(source[i]);
    if (!value) continue;
    var lower = value.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(seen, lower)) continue;
    seen[lower] = true;
    list.push(value);
  }
  var addition = normalizedCode(code);
  if (addition && !Object.prototype.hasOwnProperty.call(seen, addition.toLowerCase())) {
    list.push(addition);
  }
  return list.join(',');
}

/*
 * Ownership is both markers plus the current campaign. A gift product the
 * shopper added herself carries neither and is never a target.
 *
 * Three other shapes exist and none of them is ours. A line carrying the base
 * marker alone belongs to the cart lane, which prices its gift with an
 * automatic discount and no code: real, correct, and not ours to own, remove
 * or renew. A line carrying a marker of a campaign that is not the current one
 * depends on a code that is gone, so it is cleaned up rather than trusted. A
 * line we could not remove even if we decided to - no key - is the only shape
 * left that has to fail closed.
 */
function measureMixedMarker(cart, config) {
  var cfg = config || {};
  var ids = cfg.variantIds || [];
  var legacy = cfg.legacyCampaigns || [];
  var legacyIds = cfg.legacyVariantIds || [];
  var items = (cart && cart.items) || [];
  var out = {
    qualifyingMinor: 0,
    markerLines: [],
    suspiciousLines: [],
    unmarkedGiftLines: 0,
    nativeGiftLines: 0,
    explicitCodes: cartExplicitCodes(cart),
    codesReadable: cartCodesReadable(cart),
    unsafePricing: false,
  };

  for (var i = 0; i < items.length; i += 1) {
    var item = items[i] || {};
    var variantId = item.variant_id || item.id;
    var finalPrice = item.final_line_price;

    if (ids.indexOf(variantId) === -1) {
      /*
       * A previous campaign's gift product. Ownership here is narrower than
       * for the current product on purpose: only the full bridge signature -
       * both markers, a retired campaign key and a removable line key -
       * claims the line, and claims it for cleanup alone. Anything less is an
       * ordinary purchase and stays in the qualifying sum, so a partial or
       * forged marker can never cost her a product she chose to buy. These
       * variants never join `variantIds`: native ownership, grants and the
       * current-campaign paths cannot see them.
       */
      if (legacyIds.indexOf(variantId) !== -1) {
        var oldProps = item.properties || {};
        var oldMixed = String(oldProps[cfg.mixedPropertyName] || '');
        var oldKey = typeof item.key === 'string' ? item.key.trim() : '';
        if (String(oldProps[cfg.basePropertyName] || '') === String(cfg.basePropertyValue || '')
            && oldMixed !== ''
            && legacy.indexOf(oldMixed) !== -1
            && oldKey !== '') {
          out.markerLines.push({
            key: oldKey,
            variantId: variantId,
            quantity: Number.isSafeInteger(item.quantity) ? item.quantity : 0,
            finalMinor: finalPrice,
            originalMinor: item.original_line_price,
            safe: safeMinor(finalPrice),
            stale: true,
            legacy: true,
          });
          continue;
        }
      }
      if (!safeMinor(finalPrice)) { out.unsafePricing = true; continue; }
      // Same exclusion as the qualifying collection and the Liquid measure:
      // free_gift merchandise never funds a threshold, whichever road runs.
      if (String(item.product_type || '') !== 'free_gift') out.qualifyingMinor += finalPrice;
      continue;
    }

    var properties = item.properties || {};
    var baseMarker = String(properties[cfg.basePropertyName] || '');
    var mixedMarker = String(properties[cfg.mixedPropertyName] || '');
    var key = typeof item.key === 'string' ? item.key.trim() : '';
    var hasBase = baseMarker === String(cfg.basePropertyValue || '');
    var isCurrent = mixedMarker !== '' && mixedMarker === String(cfg.campaign || '');
    var isLegacy = mixedMarker !== '' && legacy.indexOf(mixedMarker) !== -1;

    if (!hasBase && mixedMarker === '') { out.unmarkedGiftLines += 1; continue; }
    if (hasBase && mixedMarker === '') { out.nativeGiftLines += 1; continue; }
    if (key === '') {
      out.suspiciousLines.push({ variantId: variantId });
      continue;
    }
    out.markerLines.push({
      key: key,
      variantId: variantId,
      quantity: Number.isSafeInteger(item.quantity) ? item.quantity : 0,
      finalMinor: finalPrice,
      originalMinor: item.original_line_price,
      safe: safeMinor(finalPrice),
      // Anything but the current campaign is spent, whether we recognise the
      // key or not: the code behind it can no longer be renewed.
      stale: !(hasBase && isCurrent),
      legacy: isLegacy && !isCurrent,
    });
  }
  return out;
}

/*
 * The cart page and the drawer are separate section renders and can disagree.
 * In particular, an older product-add response can arrive after the gift was
 * settled and overwrite only the drawer with pre-gift markup. Never use the
 * page's #main-cart-items as evidence for the drawer: scope the check to the
 * mini-cart element and the exact settled variant from the fresh cart read.
 */
/*
 * Every proven quantity must find a row carrying it. Rows that do not
 * render the attribute make the proof best-effort - presence and count
 * still hold - because a theme edit must degrade this check, never turn
 * it into a new way to refuse forever.
 */
function mixedQuantitiesSatisfied(proven, found) {
  for (var i = 0; i < found.length; i += 1) {
    if (found[i] === null || found[i] === undefined) return true;
  }
  var want = proven.slice().sort(function (a, b) { return a - b; });
  var have = found.slice().sort(function (a, b) { return a - b; });
  var at = 0;
  for (var j = 0; j < have.length && at < want.length; j += 1) {
    if (have[j] === want[at]) at += 1;
  }
  return at === want.length;
}

function mixedDrawerNeedsRepaint(cart, config, miniCart) {
  if (!miniCart || typeof miniCart.querySelector !== 'function') return false;
  var measured = measureMixedMarker(cart, config || {});
  if (measured.markerLines.length !== 1) return false;
  var marker = measured.markerLines[0];
  if (marker.legacy || marker.quantity !== 1 || marker.finalMinor !== 0) return false;
  // Every proven line, not just the gift: the lazy first load replaces the
  // whole drawer, and any row it dropped is the same missing-line lie.
  // Counted per variant - a properties split means two rows for one id -
  // and carrying the proven quantity, because a row at the quantity she
  // just edited away is stale about exactly that edit.
  var provenLines = {};
  (cart.items || []).forEach(function (item) {
    if (!item || !item.variant_id) return;
    var key = String(item.variant_id);
    if (!provenLines[key]) provenLines[key] = [];
    provenLines[key].push(typeof item.quantity === 'number' ? item.quantity : 1);
  });
  var linesStale = Object.keys(provenLines).some(function (id) {
    var selector = 'cart-items [data-variant-id="' + id + '"]';
    if (typeof miniCart.querySelectorAll !== 'function') {
      return !miniCart.querySelector(selector);
    }
    var rows = miniCart.querySelectorAll(selector);
    if (rows.length !== provenLines[id].length) return true;
    var found = [];
    for (var i = 0; i < rows.length; i += 1) {
      var raw = typeof rows[i].getAttribute === 'function' ? rows[i].getAttribute('data-quantity') : null;
      var parsed = raw === null || raw === undefined ? null : parseInt(raw, 10);
      found.push(Number.isFinite(parsed) ? parsed : null);
    }
    return !mixedQuantitiesSatisfied(provenLines[id], found);
  });
  if (linesStale) return true;
  // The drawer's own row list, when the element can enumerate it: a row the
  // proof does not hold - the line she just removed, a leftover split - is
  // stale in exactly the way a missing row is, and walking only the proven
  // lines never sees it. Rows that hide their variant id degrade this back
  // to the per-line walk above rather than becoming a way to refuse forever.
  if (typeof miniCart.querySelectorAll === 'function') {
    var allRows = miniCart.querySelectorAll('cart-items [data-variant-id]');
    var enumerable = allRows.length > 0;
    var foreign = false;
    for (var r = 0; r < allRows.length; r += 1) {
      var rowId = typeof allRows[r].getAttribute === 'function'
        ? allRows[r].getAttribute('data-variant-id')
        : null;
      if (!rowId) { enumerable = false; break; }
      if (!provenLines[String(rowId)]) foreign = true;
    }
    if (enumerable && foreign) return true;
  }
  // A gift the proof holds at zero-of-original must be shown discounted;
  // a drawer showing it charged is stale about the money itself. Same
  // strictness as the markup gate - mercy on one side only buys a war.
  if (marker.finalMinor === 0 && safeMinor(marker.originalMinor) && marker.originalMinor > 0) {
    if (typeof miniCart.querySelectorAll !== 'function') return false;
    var giftRows = miniCart.querySelectorAll('cart-items [data-variant-id="' + String(marker.variantId) + '"]');
    var shown = false;
    for (var g = 0; g < giftRows.length; g += 1) {
      if (typeof giftRows[g].querySelector !== 'function') continue;
      var block = giftRows[g].querySelector('.cart-item__discounted-prices');
      if (!block || typeof block.querySelectorAll !== 'function') continue;
      // Shown FREE, not merely discounted: the mark proves original != final
      // and nothing more, and a sale can park the gift at a partial discount.
      // The sale price is the block's last <bdi> - the struck original comes
      // first - and zero is format-proof: no nonzero digit.
      var prices = block.querySelectorAll('bdi');
      if (!prices || prices.length === 0) continue;
      var priceText = prices[prices.length - 1] && typeof prices[prices.length - 1].textContent === 'string'
        ? prices[prices.length - 1].textContent
        : '';
      var priceDigits = priceText.replace(/\D/g, '');
      if (priceDigits.length > 0 && !/[1-9]/.test(priceDigits)) { shown = true; break; }
    }
    if (!shown) return true;
  }
  return false;
}

function mixedDrawerRetryMs(response) {
  var raw = response && response.headers && typeof response.headers.get === 'function'
    ? response.headers.get('Retry-After')
    : '';
  var seconds = parseInt(raw, 10);
  if (!Number.isFinite(seconds) || seconds < 0) return 1000;
  return Math.min(seconds * 1000, 5000);
}

/*
 * Fetch a fresh set of the theme's own cart sections and install it through
 * MiniCart's public renderer. A burst of adds can temporarily rate-limit the
 * section request, so one Retry-After-aware retry is allowed; more would turn
 * a cosmetic repair into a request loop. The returned promise lets the mixed
 * lifecycle keep later reconciles coalesced until the repaint has converged.
 */
function renderMixedCart(options) {
  var opts = options || {};
  var doc = opts.documentTarget;
  var win = opts.windowTarget;
  var fetchFn = opts.fetchFn;
  var delayFn = typeof opts.delayFn === 'function'
    ? opts.delayFn
    : function (ms) { return new Promise(function (resolve) { setTimeout(resolve, ms); }); };
  /*
   * The patience window is long enough for the cart to change under it. A
   * snapshot fetched against yesterday's proof can pass the gift-token check
   * yet predate a newer add - installing it would hide from the shopper the
   * line she just watched arrive. The moment the proof is outdated this
   * converge stands down; the reconcile the change queued owns the repaint.
   */
  var stillCurrent = typeof opts.stillCurrent === 'function' ? opts.stillCurrent : null;

  if (!doc || !win || typeof fetchFn !== 'function') return Promise.resolve(false);
  if (doc.body && doc.body.classList && doc.body.classList.contains('template-cart')) {
    win.location.href = opts.cartUrl;
    return Promise.resolve(true);
  }

  var miniCart = doc.querySelector('mini-cart');
  if (!miniCart || typeof miniCart.getSectionsToRender !== 'function') {
    return Promise.resolve(false);
  }
  var wanted = miniCart.getSectionsToRender();
  var ids = wanted.map(function (section) { return section.section; }).filter(Boolean);
  if (ids.length === 0) return Promise.resolve(false);
  var path = win.location.pathname + '?sections=' + encodeURIComponent(ids.join(','));

  /*
   * The section endpoint can echo the cart as it was a moment ago, and a
   * repaint that installs that answer is the shopper's bug: told the gift
   * arrived, shown a list without it. The fresh cart already in hand is the
   * arbiter - when it proves a settled marked line, markup lacking that line
   * is stale and is never installed. Convergence is bounded; markup that
   * never freshens ends in an honest false, not a success over stale paint.
   */
  /*
   * The whole proven cart, not just the marked line: the drawer's lazy first
   * load replaces everything, and a snapshot that shows the gift while
   * missing the line she added a moment ago is the same lie about a
   * different row. Scoped to cart-items - recommendation buttons carry the
   * same attribute for products that are not in the cart at all.
   */
  var expectedTokens = null;
  var markerDiscountToken = null;
  if (opts.cart && opts.config) {
    // Counted per variant, not deduplicated: properties and selling plans
    // split one variant into several lines, each its own row, and a snapshot
    // holding fewer rows than the proof is missing one. Built from every
    // proven cart, marker or not - the cleanup path repaints a cart the gift
    // just left, and a gate that only wakes on a settled marker goes blind
    // exactly when the drawer still shows the removed line.
    var provenLines = {};
    (opts.cart.items || []).forEach(function (item) {
      var id = item && item.variant_id;
      if (!id) return;
      var key = String(id);
      if (!provenLines[key]) provenLines[key] = [];
      provenLines[key].push(typeof item.quantity === 'number' ? item.quantity : 1);
    });
    expectedTokens = Object.keys(provenLines).map(function (id) {
      return {
        token: 'data-variant-id="' + id + '"',
        count: provenLines[id].length,
        quantities: provenLines[id],
      };
    });
    var proof = measureMixedMarker(opts.cart, opts.config);
    if (proof.markerLines.length === 1) {
      var provenLine = proof.markerLines[0];
      if (!provenLine.legacy && provenLine.quantity === 1 && provenLine.finalMinor === 0) {
        /*
         * A snapshot rendered between the add and the code's application has
         * every variant, count and quantity - and shows the gift charged.
         * The template marks any row priced below its original with
         * cart-item__discounted-prices, so a zero-of-original proof demands
         * the gift's row carry that mark AND show a zero sale price - the
         * mark alone also fits a sale's partial discount. Deliberately
         * no fallback: the mark's absence IS the staleness signal, and an
         * asymmetric mercy here would buy a paint war against the equally
         * strict repaint decision.
         */
        markerDiscountToken = provenLine.finalMinor === 0
          && safeMinor(provenLine.originalMinor)
          && provenLine.originalMinor > 0
          ? 'data-variant-id="' + String(provenLine.variantId) + '"'
          : null;
      }
    }
  }
  /*
   * The section cache's staleness is episodic and measured in seconds, not
   * in the sub-second a three-fetch burst covers - a converge that gives up
   * inside one second loses to it on every drawer surface at once. The
   * doubling waits below span roughly 24 seconds of patience in 8 requests,
   * still bounded and still ending in an honest false when the markup never
   * freshens. A fresh first answer pays for none of it.
   */
  var MAX_FETCHES = 8;

  function convergeWaitMs(attempt) {
    return Math.min(250 * Math.pow(2, attempt), 8000);
  }

  /*
   * Only the cart-items block may vouch for a row. The recommendation
   * carousel in the same section carries the identical attribute for
   * products that are NOT in the cart - by construction, since it recommends
   * what the rendered cart lacks - and a token found there would accept the
   * very snapshot the repaint exists to refuse.
   */
  function cartItemsSegment(markup) {
    var startAt = markup.indexOf('<cart-items');
    if (startAt === -1) return '';
    var endAt = markup.indexOf('</cart-items>', startAt);
    return endAt === -1 ? markup.slice(startAt) : markup.slice(startAt, endAt);
  }

  // One entry per row tag holding the token: the quantity rendered beside
  // it, or null when the tag does not carry one.
  function rowQuantities(segment, token) {
    var rows = [];
    var at = segment.indexOf(token);
    while (at !== -1) {
      var openAt = segment.lastIndexOf('<', at);
      var closeAt = segment.indexOf('>', at);
      var tag = segment.slice(openAt === -1 ? 0 : openAt, closeAt === -1 ? segment.length : closeAt);
      var match = tag.match(/data-quantity="(\d+)"/);
      rows.push(match ? parseInt(match[1], 10) : null);
      at = segment.indexOf(token, at + token.length);
    }
    return rows;
  }

  // The discount block proves only original != final: a running sale or the
  // shopper's own code parks the gift at a partial discount between the add
  // and the apply, and that snapshot carries the mark with money still on
  // the row. The sale price is the block's last <bdi> - the struck original
  // comes first - and zero is format-proof: no nonzero digit, whatever the
  // currency renders around it.
  function someRowShowsFree(segment, token) {
    var at = segment.indexOf(token);
    while (at !== -1) {
      var next = segment.indexOf('data-variant-id=', at + token.length);
      var body = segment.slice(at, next === -1 ? segment.length : next);
      var mark = body.indexOf('cart-item__discounted-prices');
      if (mark !== -1) {
        var struck = body.indexOf('</s>', mark);
        var open = body.indexOf('<bdi>', struck === -1 ? mark : struck);
        var close = open === -1 ? -1 : body.indexOf('</bdi>', open);
        if (close !== -1) {
          var digits = body.slice(open + 5, close).replace(/\D/g, '');
          if (digits.length > 0 && !/[1-9]/.test(digits)) return true;
        }
      }
      at = segment.indexOf(token, at + token.length);
    }
    return false;
  }

  // Every row the block renders, not only the rows the proof asks about:
  // inside cart-items the attribute appears exactly once per row, on the
  // row tag itself, so counting occurrences counts rows.
  function segmentRowCount(segment) {
    var count = 0;
    var at = segment.indexOf('data-variant-id="');
    while (at !== -1) {
      count += 1;
      at = segment.indexOf('data-variant-id="', at + 1);
    }
    return count;
  }

  function freshEnough(sections) {
    if (!expectedTokens) return true;
    return ids.some(function (id) {
      var segment = cartItemsSegment(sections[id]);
      if (!segment) return false;
      // An exact multiset of rows against the proven cart: same variants,
      // same row count per variant, and nothing else. A subset check
      // approves the snapshot rendered before a removal - the extra row IS
      // the staleness. Quantities stay tolerant, but only once identity and
      // row count already match exactly.
      var counted = expectedTokens.every(function (expected) {
        var rows = rowQuantities(segment, expected.token);
        if (rows.length !== expected.count) return false;
        return mixedQuantitiesSatisfied(expected.quantities, rows);
      });
      if (!counted) return false;
      var provenRows = 0;
      for (var t = 0; t < expectedTokens.length; t += 1) provenRows += expectedTokens[t].count;
      if (segmentRowCount(segment) !== provenRows) return false;
      if (markerDiscountToken && !someRowShowsFree(segment, markerDiscountToken)) return false;
      return true;
    });
  }

  function install(sections) {
    var details = doc.querySelector('cart-drawer details');
    if (details && details.hasAttribute('open') && typeof miniCart.renderContents === 'function') {
      miniCart.renderContents({ id: miniCart.productId, sections: sections });
      return true;
    }
    if (typeof miniCart.getSectionInnerHTML !== 'function') return false;
    var painted = false;
    wanted.forEach(function (section) {
      var element = doc.getElementById(section.id);
      if (!element) return;
      element.innerHTML = miniCart.getSectionInnerHTML(sections[section.section], section.selector);
      painted = true;
    });
    return painted;
  }

  function request(attempt) {
    return Promise.resolve()
      .then(function () { return fetchFn(path, { headers: { Accept: 'application/json' } }); })
      .then(function (response) {
        if (stillCurrent && !stillCurrent()) return false;
        if (response && response.status === 429 && attempt + 1 < MAX_FETCHES) {
          return Promise.resolve(delayFn(mixedDrawerRetryMs(response)))
            .then(function () { return request(attempt + 1); });
        }
        if (!response || !response.ok) return false;
        return response.json().then(function (sections) {
          if (!sections) return false;
          var complete = ids.every(function (id) { return typeof sections[id] === 'string'; });
          if (!complete) return false;
          if (!freshEnough(sections)) {
            if (attempt + 1 < MAX_FETCHES) {
              return Promise.resolve(delayFn(convergeWaitMs(attempt)))
                .then(function () { return request(attempt + 1); });
            }
            return false;
          }
          // Checked again on this side of the body read: on the in-app
          // browser's connections json() is real time, and an add landing
          // inside it must not be painted over.
          if (stillCurrent && !stillCurrent()) return false;
          return install(sections);
        });
      })
      .catch(function () { return false; });
  }

  return request(0);
}

/*
 * Both native lanes keep absolute priority. The bridge exists only for the
 * order that is itself the crossing, so either lane already clearing the
 * threshold on its own is a hard no.
 */
function mixedEligibility(measured, context) {
  var ctx = context || {};
  if (ctx.isLoggedIn !== true) return { eligible: false, reason: 'logged-out' };
  if (ctx.isMember !== true) return { eligible: false, reason: 'not-member' };
  if (ctx.hasRedeemed === true) return { eligible: false, reason: 'redeemed' };
  var threshold = ctx.thresholdMinor;
  var spend = ctx.spendMinor;
  var qualifying = measured ? measured.qualifyingMinor : null;
  if (!safeMinor(threshold) || threshold <= 0) return { eligible: false, reason: 'no-threshold' };
  if (!safeMinor(spend)) return { eligible: false, reason: 'no-accrual' };
  if (!safeMinor(qualifying)) return { eligible: false, reason: 'unsafe-cart' };
  if (spend >= threshold) return { eligible: false, reason: 'native-accrual-lane' };
  if (qualifying >= threshold) return { eligible: false, reason: 'native-cart-lane' };
  if (spend <= 0) return { eligible: false, reason: 'no-accrual' };
  if (qualifying <= 0) return { eligible: false, reason: 'empty-cart' };
  if (spend + qualifying < threshold) return { eligible: false, reason: 'below-threshold' };
  /*
   * Last, and only once every substantive gate has passed. The repair path
   * spares a line whose sole failure is the shelf, so a shopper who has left
   * the mixed band for a real reason must report that reason instead - or her
   * marker would be repaired, and a grant minted, on a cart that no longer
   * qualifies for one.
   */
  if (ctx.giftAvailable === false) return { eligible: false, reason: 'out-of-stock' };
  return { eligible: true, reason: 'mixed-candidate' };
}

function decideMixedGift(measured, context) {
  var gate = mixedEligibility(measured, context);
  // `reason` tracks the line's own state once one is found, so the gate's
  // verdict is kept separately - the two answer different questions.
  var out = {
    state: 'no_marker', reason: gate.reason,
    eligible: gate.eligible, eligibilityReason: gate.reason,
  };
  if (!measured) { out.state = 'blocked'; out.reason = 'no-measurement'; return out; }
  if (measured.suspiciousLines.length > 0) {
    out.state = 'blocked'; out.reason = 'suspicious-marker'; return out;
  }
  if (measured.markerLines.length === 0) {
    // The cart lane holds this cart. It answers for its own gift, so the only
    // correct move here is to get out of the way - including out of the way of
    // her checkout button.
    if (measured.nativeGiftLines > 0) {
      out.state = 'native_lane'; out.reason = 'native-gift-line';
    }
    return out;
  }
  if (measured.markerLines.length > 1) {
    out.state = 'blocked'; out.reason = 'duplicate-marker'; return out;
  }
  var marker = measured.markerLines[0];
  if (marker.stale) {
    out.state = 'needs_cleanup';
    out.reason = marker.legacy ? 'legacy-campaign' : 'stale-campaign';
    return out;
  }
  if (marker.quantity !== 1) { out.state = 'blocked'; out.reason = 'marker-quantity'; return out; }
  if (!marker.safe) { out.state = 'blocked'; out.reason = 'unsafe-price'; return out; }
  if (marker.finalMinor === 0) {
    /*
     * The discount targets the product, not a line, and gives away one unit of
     * it. With a second line of the same product in the cart - bought outright,
     * or the cart lane's own gift - Shopify decides which of the two the
     * hundred percent lands on
     * - and it may zero the one she paid for and charge her for the gift. A
     * zero we cannot attribute is not a zero we may promise, so this fails
     * closed rather than guessing. A charged marker with the same neighbour is
     * left to the cleanup path below: taking the gift off is better for her
     * than a locked checkout.
     */
    if (measured.unmarkedGiftLines > 0 || measured.nativeGiftLines > 0) {
      out.state = 'blocked'; out.reason = 'ambiguous-gift-lines'; return out;
    }
    out.state = 'verified_zero'; out.reason = 'zero'; return out;
  }
  out.state = 'needs_repair';
  out.reason = 'charged';
  return out;
}

/*
 * The cart is not a trustworthy witness to its own discounts: half an hour in
 * a background tab and it reported a dead code as applicable while checkout
 * charged full price for the line. The grant tells us when it dies, so that is
 * what we ask. An expiry we never learned is not evidence of anything, so it
 * falls back to the cart rather than buying a grant on every page view.
 */
var MIXED_GRANT_MARGIN_MS = 120000;
// Entering card details takes minutes, so the last gate before money moves
// asks for real runway rather than a code that is technically still alive.
var MIXED_CHECKOUT_MARGIN_MS = 600000;

function mixedGrantExpired(expiresAt, nowMs, marginMs) {
  if (expiresAt === undefined || expiresAt === null || expiresAt === '') return false;
  var at = Date.parse(expiresAt);
  // Unparseable is the one unknown that counts as expired: something answered,
  // and it was not a time.
  if (!isFinite(at)) return true;
  var now = typeof nowMs === 'number' && isFinite(nowMs) ? nowMs : Date.now();
  var margin = typeof marginMs === 'number' && isFinite(marginMs) ? marginMs : MIXED_GRANT_MARGIN_MS;
  return now + margin >= at;
}

function mixedBlocked(reason) {
  return { outcome: 'blocked', reason: reason, checkoutLocked: true };
}

function mixedRead(deps, phase) {
  return Promise.resolve()
    .then(function () { return deps.readCart(phase); })
    .then(function (cart) { return { ok: true, cart: cart }; })
    .catch(function () { return { ok: false, cart: null }; });
}

/*
 * Shopify may re-key a line whenever its properties or discounts change, so
 * the key held from an earlier read is a stale address. Every removal starts
 * from a fresh read, and a lost mutation response is resolved by reading
 * again rather than by assuming either outcome.
 */
function cleanupMixedMarker(deps, config, successOutcome, reason) {
  return mixedRead(deps, 'before-cleanup').then(function (read) {
    if (!read.ok) return mixedBlocked('read-failed');
    var measured = measureMixedMarker(read.cart, config);
    if (measured.suspiciousLines.length > 0) return mixedBlocked('suspicious-marker');
    if (measured.markerLines.length === 0) return { outcome: successOutcome, reason: reason };
    if (measured.markerLines.length > 1) return mixedBlocked('duplicate-marker');
    return Promise.resolve()
      .then(function () { return deps.removeLine(measured.markerLines[0].key); })
      .catch(function () { return null; })
      .then(function () { return mixedRead(deps, 'after-cleanup'); })
      .then(function (after) {
        if (!after.ok) return mixedBlocked('read-failed');
        if (measureMixedMarker(after.cart, config).markerLines.length > 0) {
          return mixedBlocked('marker-still-present');
        }
        if (typeof deps.renderCart === 'function') deps.renderCart(after.cart);
        return { outcome: successOutcome, reason: reason };
      });
  });
}

function verifyMixedZero(deps, config, phase) {
  return mixedRead(deps, phase).then(function (read) {
    if (!read.ok) return { ok: false, blocked: true, measured: null };
    var measured = measureMixedMarker(read.cart, config);
    // Only the state of the line matters here, and that is independent of who
    // is looking at it: an empty context asks purely "what is this line?".
    var decision = decideMixedGift(measured, {});
    if (decision.state === 'blocked') return { ok: false, blocked: true, measured: measured };
    return {
      ok: decision.state === 'verified_zero',
      blocked: false,
      // A cart the other lane holds has no line of ours in it, which is the
      // same fact as an empty cart for everything downstream.
      missing: decision.state === 'no_marker' || decision.state === 'native_lane',
      measured: measured,
      cart: read.cart,
    };
  });
}

function grantAndVerifyMixed(input, deps, config, isClaim) {
  return Promise.resolve()
    .then(function () {
      var grantRequest = {
        action: 'grant',
        gift_product_id: String(input.giftProductId || ''),
      };
      // The gate declares where it calls from; the lease policy itself lives
      // in the worker, never in a browser-supplied number.
      if (input.intent === 'checkout') grantRequest.intent = 'checkout';
      return deps.requestGrant(grantRequest);
    })
    .catch(function () { return null; })
    .then(function (raw) {
      // Shape, outcome and expiry are wrong no matter what the cart holds, so
      // they fail fast. Only the gap has to wait for a fresh measurement.
      var signed = raw && GRANT_OUTCOMES.indexOf(String(raw.outcome || '')) !== -1
        && checkedMixedGrant(raw, {
          thresholdMinor: input.thresholdMinor,
          qualifyingMinor: Number.MAX_SAFE_INTEGER,
        })
        ? raw
        : null;
      if (!signed) {
        return isClaim
          ? { outcome: 'not_applicable', reason: 'grant-denied' }
          : cleanupMixedMarker(deps, config, 'removed_ineligible', 'grant-denied');
      }
      // Recorded before anything is applied. The next run measures freshness
      // against this, not against whatever the cart chooses to report.
      if (typeof deps.rememberGrant === 'function') deps.rememberGrant(signed.expiresAt);
      /*
       * The safety boundary: the gate never trusts the lease promise. An
       * answer that still dies inside the checkout margin - the worker could
       * not extend, or was never asked - takes the gift off rather than
       * letting money move against a code about to expire.
       */
      if (!isClaim && input.intent === 'checkout'
          && mixedGrantExpired(signed.expiresAt, input.nowMs, MIXED_CHECKOUT_MARGIN_MS)) {
        return cleanupMixedMarker(deps, config, 'removed_unverified', 'thin-runway');
      }
      var added = isClaim
        ? Promise.resolve()
          .then(function () {
            var properties = {};
            properties[config.basePropertyName] = config.basePropertyValue;
            properties[config.mixedPropertyName] = config.campaign;
            return deps.addGift(input.variantId, properties);
          })
          // A lost add response is not a verdict. The next read is.
          .catch(function () { return null; })
        : Promise.resolve();

      return added
        .then(function () { return verifyMixedZero(deps, config, isClaim ? 'after-add' : 'before-grant'); })
        .then(function (state) {
          if (state.blocked) return mixedBlocked('unverifiable');
          if (state.ok) {
            // Renewal does not launder attribution: at the gate a zero whose
            // discounts cannot be read still may not carry money, however
            // fresh the grant. The same rule the gate enforces on entry holds
            // on the way out of this detour.
            if (input.intent === 'checkout' && !state.measured.codesReadable) {
              return cleanupMixedMarker(deps, config, 'removed_unverified', 'opaque-discounts');
            }
            // A dormant code Shopify reapplied by itself lands the line at zero
            // with nothing left to apply. The mutation ran against the JSON
            // endpoint, which answers with no markup, so this is the only
            // chance the drawer gets to learn the line exists.
            if (typeof deps.renderCart === 'function') deps.renderCart(state.cart);
            return { outcome: 'verified_zero', reason: 'zero' };
          }
          if (state.missing) return { outcome: 'not_applicable', reason: 'marker-missing' };
          if (!state.measured.codesReadable) {
            return cleanupMixedMarker(deps, config, 'removed_unverified', 'opaque-discounts');
          }
          // The gap is validated against the cart as it is now, not as it was
          // when the trigger fired. A cart that shrank in between cannot
          // satisfy the Buy condition, so applying would only charge her.
          var grant = checkedMixedGrant(signed, {
            thresholdMinor: input.thresholdMinor,
            qualifyingMinor: state.measured.qualifyingMinor,
          });
          if (!grant) {
            return cleanupMixedMarker(deps, config, 'removed_unverified', 'gap-mismatch');
          }
          var payload = mergeDiscountCodes(state.measured.explicitCodes, grant.code);
          if (!payload) return cleanupMixedMarker(deps, config, 'removed_unverified', 'empty-payload');
          return Promise.resolve()
            .then(function () { return deps.applyCodes(payload); })
            // Same rule as the add: the mutation response is never the verdict.
            .catch(function () { return null; })
            .then(function () { return verifyMixedZero(deps, config, 'after-apply'); })
            .then(function (after) {
              if (after.blocked) return mixedBlocked('unverifiable');
              if (after.ok) {
                if (typeof deps.renderCart === 'function') deps.renderCart(after.cart);
                return { outcome: 'verified_zero', reason: 'zero' };
              }
              if (after.missing) return { outcome: 'not_applicable', reason: 'marker-missing' };
              return cleanupMixedMarker(deps, config, 'removed_unverified', 'still-charged');
            });
        });
    });
}

/*
 * The other lane's gift is not ours to own - but stepping aside is only safe
 * where the other lane still gets its turn. While she browses it does: the
 * sync that follows this answer runs the native rules and takes a charged gift
 * off. The checkout gate has no such fallthrough - it reads the cart, hears
 * that nothing here is ours, and submits - so the gate asks the native rule
 * itself. Anything short of "she is about to pay for a gift" stays out of the
 * way; that one case is cleaned up and proved before checkout is released.
 */
function nativeLaneVerdict(input, deps, config, reason) {
  var aside = { outcome: 'not_applicable', reason: reason };
  if (input.intent !== 'checkout') return Promise.resolve(aside);
  var native = decideGift(measureCart(input.cart, config.variantIds || []), {
    thresholdMinor: input.thresholdMinor,
    isMember: input.isMember,
    isLoggedIn: input.isLoggedIn,
    hasRedeemed: input.hasRedeemed,
    spendMinor: input.spendMinor,
    giftAvailable: input.giftAvailable,
  });
  if (native.action !== 'remove-gift' || native.reason !== 'charged') return Promise.resolve(aside);
  if (typeof deps.removeNativeGift !== 'function') return Promise.resolve(mixedBlocked('native-charged'));
  return Promise.resolve()
    .then(function () { return deps.removeNativeGift(); })
    .then(function () { return mixedRead(deps, 'after-cleanup'); })
    .then(function (after) {
      if (!after.ok) return mixedBlocked('read-failed');
      var still = decideGift(measureCart(after.cart, config.variantIds || []), {
        thresholdMinor: input.thresholdMinor,
        isMember: input.isMember,
        isLoggedIn: input.isLoggedIn,
        hasRedeemed: input.hasRedeemed,
        spendMinor: input.spendMinor,
        giftAvailable: input.giftAvailable,
      });
      if (still.action === 'remove-gift' && still.reason === 'charged') {
        return mixedBlocked('native-still-charged');
      }
      return { outcome: 'removed_ineligible', reason: 'native-charged' };
    })
    .catch(function () { return mixedBlocked('native-cleanup-failed'); });
}

/*
 * One reconciler owns the marked line. Claim and repair differ only in
 * whether the line has to be added first.
 */
function reconcileMixedGift(input, deps) {
  var d = deps || {};
  if (!input || mixedModeOf(input.mode) !== 'canary' || input.isCanary !== true) {
    return Promise.resolve({ outcome: 'not_applicable', reason: 'mode' });
  }
  var config = input.config || {};
  var measured = measureMixedMarker(input.cart, config);
  var decision = decideMixedGift(measured, input);

  if (decision.state === 'blocked') return Promise.resolve(mixedBlocked(decision.reason));
  if (decision.state === 'native_lane') {
    return nativeLaneVerdict(input, d, config, decision.reason);
  }
  if (decision.state === 'verified_zero') {
    // A zero line whose grant has run out is the exact shape she hit: the cart
    // still showed the gift free and checkout charged for it. Renew before
    // believing the price.
    var atGate = input.intent === 'checkout';
    var margin = atGate ? MIXED_CHECKOUT_MARGIN_MS : MIXED_GRANT_MARGIN_MS;
    // An expiry nobody can see (storage wiped, cart opened on another device)
    // is fine while she browses, but the gate may not move money on a guess:
    // unknown counts as expired there and the grant is renewed first.
    var expiryUnknown = input.grantExpiresAt === undefined
      || input.grantExpiresAt === null || input.grantExpiresAt === '';
    if (mixedGrantExpired(input.grantExpiresAt, input.nowMs, margin) || (atGate && expiryUnknown)) {
      return grantAndVerifyMixed(input, d, config, false);
    }
    /*
     * A zero we can see but cannot explain is harmless while she browses. At
     * the gate it is not: money is about to move against a cart whose discount
     * state would not parse, and checkout prices it again on its own terms.
     * The gift comes off rather than the checkout locking - she loses a gift,
     * not the ability to buy.
     */
    if (atGate && !measured.codesReadable) {
      return cleanupMixedMarker(d, config, 'removed_unverified', 'opaque-discounts');
    }
    return Promise.resolve({ outcome: 'verified_zero', reason: 'zero' });
  }
  if (decision.state === 'needs_cleanup') {
    return cleanupMixedMarker(d, config, 'removed_ineligible', decision.reason);
  }
  if (decision.state === 'needs_repair') {
    /*
     * Repairing beside the other lane's gift would buy her a second one and
     * hand Shopify the choice of which to zero. Ours comes off instead.
     *
     * An empty shelf is the one ineligibility that does not remove anything:
     * it answers whether she may be offered a gift, never whether the one she
     * is already holding stays. Every other reason - membership, redemption,
     * a hold - is about her, and those still clean up.
     */
    if ((!decision.eligible && decision.eligibilityReason !== 'out-of-stock')
        || measured.nativeGiftLines > 0) {
      return cleanupMixedMarker(d, config, 'removed_ineligible', decision.reason);
    }
    if (!measured.codesReadable) {
      return cleanupMixedMarker(d, config, 'removed_ineligible', 'opaque-discounts');
    }
    return grantAndVerifyMixed(input, d, config, false);
  }
  if (input.intent !== 'claim') {
    return Promise.resolve({ outcome: 'not_applicable', reason: decision.reason });
  }
  if (measured.unmarkedGiftLines > 0) {
    return Promise.resolve({ outcome: 'not_applicable', reason: 'gift-product-present' });
  }
  if (!measured.codesReadable) {
    return Promise.resolve({ outcome: 'not_applicable', reason: 'opaque-discounts' });
  }
  if (!decision.eligible) {
    return Promise.resolve({ outcome: 'not_applicable', reason: decision.reason });
  }
  return grantAndVerifyMixed(input, d, config, true);
}

/*
 * Returning to a suspended tab is the main path for this store, not an edge:
 * three quarters of cart traffic is the in-app browser. So the reconciler
 * reruns on restore rather than trusting anything held in memory.
 */
/*
 * The fallback reason still identifies a coalesced turn, but it no longer
 * carries every obligation by itself. Restore work, permission to prompt and
 * drawer convergence are merged independently below, so keeping a restore as
 * the fallback cannot erase a cart change's permission to open the dialog.
 *
 * `restore` is deliberately not a restore here: on the bridge-off path the
 * shelf is already re-asked before reconciliation is requested.
 */
var REASON_RANK = { pageshow: 2, visibility: 2, 'cart-change': 1, return: 1 };
var PROMPT_REASON_RANK = { 'cart-change': 1, return: 2 };

/*
 * Of two remembered expiries, the one that comes first. Either lane may have
 * left a grant behind, and a page that watches only one clock sleeps through
 * the other. Unparseable or absent values simply lose; nothing is invented.
 */
function earliestExpiry(a, b) {
  var at = Date.parse(String(a || ''));
  var bt = Date.parse(String(b || ''));
  if (!isFinite(at)) return isFinite(bt) ? b : null;
  if (!isFinite(bt)) return a;
  return bt < at ? b : a;
}

/*
 * The earliest remembered clock still ahead of a moment. Once one clock has
 * fired, the page keeps watching whatever is left rather than going quiet.
 */
function nextExpiryAfter(expiries, afterMs) {
  var best = null;
  var bestAt = Infinity;
  for (var i = 0; i < (expiries || []).length; i += 1) {
    var at = Date.parse(String(expiries[i] || ''));
    if (!isFinite(at) || at <= afterMs || at >= bestAt) continue;
    best = expiries[i];
    bestAt = at;
  }
  return best;
}

function keptReason(queued, next) {
  if (queued === null || queued === undefined) return next;
  return (REASON_RANK[queued] || 0) > (REASON_RANK[next] || 0) ? queued : next;
}

function keptPromptReason(queued, next) {
  if (queued === null || queued === undefined) return next;
  if (next === null || next === undefined) return queued;
  return (PROMPT_REASON_RANK[queued] || 0) > (PROMPT_REASON_RANK[next] || 0) ? queued : next;
}

function syncWorkFor(value) {
  if (value && typeof value === 'object') {
    return {
      fallbackReason: value.fallbackReason || 'reconcile',
      restoreNeeded: value.restoreNeeded === true,
      promptReason: value.promptReason || null,
      drawerNeeded: value.drawerNeeded === true,
    };
  }
  var reason = value || 'reconcile';
  return {
    fallbackReason: reason,
    restoreNeeded: reason === 'pageshow' || reason === 'visibility',
    promptReason: reason === 'cart-change' || reason === 'return' ? reason : null,
    drawerNeeded: reason === 'drawer',
  };
}

function mergeSyncWork(current, next) {
  var incoming = syncWorkFor(next);
  if (current === null || current === undefined) return incoming;
  var held = syncWorkFor(current);
  return {
    fallbackReason: keptReason(held.fallbackReason, incoming.fallbackReason),
    restoreNeeded: held.restoreNeeded || incoming.restoreNeeded,
    promptReason: keptPromptReason(held.promptReason, incoming.promptReason),
    drawerNeeded: held.drawerNeeded || incoming.drawerNeeded,
  };
}

function triggerForSyncWork(work) {
  return work.promptReason || work.fallbackReason;
}

function createMixedLifecycle(options) {
  var opts = options || {};
  var enabled = opts.enabled === true;
  var run = typeof opts.run === 'function' ? opts.run : null;

  function request(reason) {
    if (!enabled || !run) return Promise.resolve();
    var started;
    try { started = run(reason); } catch (err) { started = null; }
    return Promise.resolve(started).catch(function () { return null; });
  }

  function start() {
    if (!enabled) return;
    var w = opts.windowTarget;
    var doc = opts.documentTarget;
    if (w && typeof w.addEventListener === 'function') {
      w.addEventListener('pageshow', function () { request('pageshow'); });
    }
    if (doc && typeof doc.addEventListener === 'function') {
      doc.addEventListener('visibilitychange', function () {
        if (doc.visibilityState === 'visible') request('visibility');
      });
    }
  }

  var setTimer = typeof opts.setTimeoutFn === 'function'
    ? opts.setTimeoutFn
    : function (fn, ms) { return setTimeout(fn, ms); };
  var clearTimer = typeof opts.clearTimeoutFn === 'function'
    ? opts.clearTimeoutFn
    : function (id) { clearTimeout(id); };
  var readNow = typeof opts.nowFn === 'function'
    ? opts.nowFn
    : function () { return Date.now(); };
  var expiryTimer = null;
  var expiryWatch = null;

  /*
   * UX only, never a safety boundary: the server re-checks entitlement on
   * every grant and the checkout gate never trusts this clock. The watch
   * exists so a shopper who is visible and idle hears that her code died
   * instead of discovering it at the gate. One watch at a time - a newer
   * expiry replaces the older one. Two disciplines guard the callback
   * itself: identity, because clearTimeout is best-effort once a throttling
   * browser has already queued the fire, so only the callback that IS the
   * current watch may act; and visibility, because a hidden tab does zero
   * work - the resume path already owns exactly one evaluation, and a
   * hidden fire that also ran would make it two.
   */
  function watchExpiry(expiresAt) {
    if (!enabled || !run) return;
    var at = Date.parse(String(expiresAt || ''));
    if (!isFinite(at)) return;
    if (expiryTimer !== null) {
      clearTimer(expiryTimer);
      expiryTimer = null;
    }
    var watch = {};
    expiryWatch = watch;
    var delay = at - readNow();
    expiryTimer = setTimer(function () {
      if (expiryWatch !== watch) return;
      expiryWatch = null;
      expiryTimer = null;
      var doc = opts.documentTarget;
      if (doc && doc.visibilityState === 'hidden') return;
      request('expiry');
    }, delay > 0 ? delay : 0);
  }

  return { start: start, request: request, watchExpiry: watchExpiry };
}

/*
 * The last gate before money moves. It blocks first and asks afterwards, and
 * a verified cleanup deliberately does not continue on its own: the shopper
 * is told the gift came off and decides whether to buy without it.
 */
function createCheckoutCoordinator(options) {
  var opts = options || {};
  var enabled = opts.enabled === true;
  var cleared = false;
  var inFlight = null;
  // Bumped by every reset. A verdict formed before a reset was formed on a
  // cart that has since moved; it is verified once more before it may
  // release the form, so one attempt at a time is also one verdict at a time.
  var generation = 0;

  function verify() {
    var gen = generation;
    return Promise.resolve()
      .then(function () { return opts.ensure(); })
      // A failure inside the verification holds this press; whether it also
      // locks the gate until a reload is the lane's call (`lockOnFailure`).
      .catch(function () { return { outcome: 'blocked', checkoutLocked: opts.lockOnFailure !== false }; })
      .then(function (result) {
        // Only a releasable verdict needs to be fresh. A cleanup or a block
        // is an answer in itself, and the gate's own write is what moved the
        // cart; it is reported, never retried into an automatic release.
        var outcome = result && result.outcome;
        var releasable = outcome === 'verified_zero' || outcome === 'not_applicable';
        if (releasable && gen !== generation) return { outcome: 'stale' };
        return result;
      });
  }

  function handle(event, target) {
    if (!enabled || cleared) return Promise.resolve();
    // Prevented before anything else, including on presses that join a
    // verification already running: an unprevented press posts the cart on its
    // own and walks straight past the gate.
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    // The verdict travels over the network, so an impatient thumb has a real
    // window to press again. Later presses wait on the first answer rather
    // than opening a second verification - and a second submit.
    if (inFlight) return inFlight;
    var attempt = verify()
      .then(function (result) {
        // A cart event arrived while the verdict was forming: one fresh look,
        // and a verdict that is stale again is dropped without a release.
        if (result && result.outcome === 'stale') return verify();
        return result;
      })
      .then(function (result) {
        var outcome = result && result.outcome;
        if (outcome === 'stale') return;
        if (outcome === 'verified_zero' || outcome === 'not_applicable') {
          cleared = true;
          if (typeof opts.submit === 'function') opts.submit(target);
          return;
        }
        if (typeof opts.notify !== 'function') return;
        opts.notify(
          outcome === 'removed_ineligible' || outcome === 'removed_unverified' || outcome === 'removed_floor'
            ? 'removed'
            : 'error',
          result,
        );
      });
    // The stored promise is the one that clears itself, so every press waits on
    // the same thing and a later press is a fresh attempt rather than a replay
    // of an answer that is no longer current.
    inFlight = attempt.then(
      function (value) { inFlight = null; return value; },
      function (error) { inFlight = null; throw error; },
    );
    return inFlight;
  }

  // The gate closes again, and the attempt in flight (if any) learns that
  // its cart has moved; it is not dropped, so a press meanwhile still joins
  // it rather than opening a second verification.
  function reset() { cleared = false; generation += 1; }

  return { handle: handle, reset: reset };
}

function safeMixedProxyEndpoint(raw, origin) {
  if (!raw || !origin) return '';
  try {
    var url = new URL(raw, origin);
    if (url.origin !== origin || url.username || url.password || url.hash) return '';
    if (url.pathname.indexOf('/apps/') !== 0) return '';
    return url.pathname + url.search;
  } catch (e) {
    return '';
  }
}

function checkedMixedGrant(raw, expected) {
  var successfulOutcomes = ['created', 'reused', 'recovered'];
  if (!raw || successfulOutcomes.indexOf(raw.outcome) === -1) return null;
  var code = typeof raw.code === 'string' ? raw.code : '';
  var gapMinor = mixedMinorValue(raw.requiredPurchaseMinor, false);
  var expiresAt = Date.parse(String(raw.expiresAt || ''));
  if (!/^KCG-[0-9A-F]{24}$/i.test(code)) return null;
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) return null;
  if (gapMinor === null || gapMinor <= 0 || gapMinor >= expected.thresholdMinor) return null;
  if (expected.qualifyingMinor < gapMinor) return null;
  return { code: code, gapMinor: gapMinor };
}

var FLOOR_CODE_RE = /^KCF-[0-9A-F]{24}$/i;

function isFloorReason(reason) {
  return reason === 'entitled-floor' || reason === 'entitled-floor-by-spend';
}

/*
 * The Worker's answer to a floor grant. Only its own prefix is accepted: a
 * gift code here would mean the wrong benefit was minted. The percent is the
 * server's number and is the only source the storefront copy may quote.
 */
function checkedFloorGrant(raw) {
  var successfulOutcomes = ['created', 'reused', 'recovered'];
  if (!raw || successfulOutcomes.indexOf(raw.outcome) === -1) return null;
  if (raw.benefit !== 'floor') return null;
  var code = typeof raw.code === 'string' ? raw.code.trim() : '';
  if (!FLOOR_CODE_RE.test(code)) return null;
  var percent = raw.percent;
  if (typeof percent !== 'number' || !isFinite(percent) || percent !== Math.floor(percent)) return null;
  if (percent < 1 || percent > 100) return null;
  var expiresAt = Date.parse(String(raw.expiresAt || ''));
  if (!isFinite(expiresAt) || expiresAt <= Date.now()) return null;
  return { code: code, percent: percent, expiresAt: raw.expiresAt };
}

function floorCodesIn(cart) {
  return cartExplicitCodes(cart).filter(function (code) { return FLOOR_CODE_RE.test(code); });
}

/*
 * Only Shopify says whether the code did anything. The cart's own
 * `applicable` flag is the first word; failing that, a cart-level application
 * keyed by the code. A code merely listed proves nothing - that is exactly
 * the case where her own non-combining code won and the floor yielded.
 */
function floorApplied(cart, code) {
  if (!cart || !cartCodesReadable(cart)) return false;
  var wanted = normalizedCode(code).toLowerCase();
  if (!wanted) return false;
  var entries = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
  var listed = null;
  for (var i = 0; i < entries.length; i += 1) {
    if (normalizedCode(entries[i].code).toLowerCase() === wanted) { listed = entries[i]; break; }
  }
  if (!listed) return false;
  if (listed.applicable === true) return true;
  if (listed.applicable === false) return false;
  var applications = Array.isArray(cart.cart_level_discount_applications)
    ? cart.cart_level_discount_applications : [];
  for (var j = 0; j < applications.length; j += 1) {
    var app = applications[j];
    if (!app || typeof app !== 'object') continue;
    var key = normalizedCode(app.key || app.title).toLowerCase();
    if (key === wanted) return true;
  }
  return false;
}

/*
 * A floor code belongs in the cart for exactly as long as the floor is the
 * road: a size back on the shelf, a cart under the line, a gift already
 * redeemed - each one makes the code a stray that must come off before
 * anything else is offered. `remaining` is the full rewrite list, every
 * other code kept, so her own code is never the collateral.
 */
function floorCodeVerdict(cart, decision, giftAvailable) {
  if (!cart || !cartCodesReadable(cart)) return { present: false, action: 'none', remaining: null };
  var codes = floorCodesIn(cart);
  var present = codes.length > 0;
  if (!present) return { present: false, action: 'none', remaining: null };
  var entitled = decision && decision.action === 'ask-member' && isFloorReason(decision.reason)
    && giftAvailable !== true;
  // Entitlement alone does not confirm a code: only a code the cart still
  // honours is kept. An expired one, or one her own code beat, comes off
  // like any stray, and the offer is made again from a clean cart. Two
  // floor codes at once are strays as well: the Worker issues one per
  // customer, and the offer re-asks for the right one from a clean cart.
  var honoured = codes.length === 1 && floorApplied(cart, codes[0]);
  if (entitled && honoured) return { present: true, action: 'keep', remaining: null };
  var remaining = cartExplicitCodes(cart).filter(function (code) { return !FLOOR_CODE_RE.test(code); });
  return { present: true, action: 'remove', remaining: remaining.join(',') };
}

/*
 * Whether this moment may open the dialog, or the banner must carry the offer
 * alone. The two roads interrupt differently. The cart road answers the cart
 * she just edited, so it opens on cart changes. The accrual road is a standing
 * fact rather than an event: it introduces itself once - the first moment it
 * is seen - and from then on only the strip in the cart speaks, however many
 * carts she edits. Coming back from the join journey reopens either road,
 * because the interruption is the answer she left to get.
 */
function decidePrompt(decision, options) {
  var trigger = options.trigger;
  var promptedBefore = options.promptedBefore === true;
  var asks = decision.action === 'ask-member'
    || decision.action === 'ask-join'
    || decision.action === 'ask-account';
  if (!asks) return { open: false, remember: false };
  if (decision.reason === 'entitled-by-spend' || decision.reason === 'entitled-floor-by-spend') {
    var open = trigger === 'return' || !promptedBefore;
    return { open: open, remember: open };
  }
  return { open: trigger === 'cart-change' || trigger === 'return', remember: false };
}

/*
 * A gift the discount engine failed to make free is removed - and the very
 * next cart event would add, charge and remove it again in a loop. One
 * charged removal therefore stands the offer down until the page is loaded
 * afresh. Nothing else locks, and nothing unlocks short of a reload.
 */
function offerLockout(locked, decision) {
  if (locked === true) return true;
  return Boolean(decision && decision.action === 'remove-gift' && decision.reason === 'charged');
}

/* ------------------------------------------------------------------ */

function start() {
  var root = document.querySelector('[data-club-gift]');
  if (!root || root.dataset.bound === 'true') return;
  root.dataset.bound = 'true';

  var THRESHOLD = parseInt(root.dataset.thresholdMinor, 10) || 0;
  // The section's master switch. Off, the runtime is retire-only: a code
  // issued before the campaign was turned off is still taken out of the
  // cart and still holds the gate, but nothing is offered and no native
  // decision runs; a gift line in the cart is Shopify's business then.
  var CAMPAIGN_ON = root.dataset.campaignEnabled !== 'false';
  var IS_MEMBER = root.dataset.member === 'true';
  var IS_LOGGED_IN = root.dataset.loggedIn === 'true';
  var HAS_REDEEMED = root.dataset.redeemed === 'true';
  // Absent means unknown, and unknown keeps the campaign running. Only an
  // explicit false - every size sold out when the page rendered - silences it.
  var GIFT_AVAILABLE = root.dataset.giftAvailable !== 'false';
  var GIFT_HANDLE = root.dataset.giftHandle || '';
  /*
   * The render-time flag ages: the last size can sell to someone else while
   * this page sits in a background tab. `null` means nothing has been learned
   * since and the render still stands; a boolean is what the shop said after.
   */
  var stockLearned = null;

  function giftAvailableNow() {
    return stockLearned === null ? GIFT_AVAILABLE : stockLearned;
  }

  /*
   * Asked when staleness is likely - a restored tab - and when a size has just
   * refused to go in the cart. Never on the way into a dialog: a fetch there
   * would put a network hop between her tap and the picker. A failure here
   * leaves the render's answer standing, because a lost request is not
   * evidence that the shelf is empty.
   */
  // Restores fire `pageshow` and `visibilitychange` together, and a claim
  // error can land on top of both. Answers are applied in the order they were
  // asked for, never the order they arrive: the same rule the cart sync uses.
  var stockAsked = 0;
  // A request that answered with nothing has nothing to supersede, so the
  // generation advances on applied answers rather than on attempts: a failed
  // newer request must not throw away an older one that came back good.
  var stockApplied = 0;

  function refreshGiftAvailability() {
    if (!GIFT_HANDLE) return Promise.resolve();
    var ticket = stockAsked += 1;
    var base = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/')
      .replace(/\/$/, '');
    /*
     * This fetch runs ahead of the scheduler's cart read, where the
     * stalled-read guard cannot see it, so it carries its own deadline. The
     * timer lives until the whole chain settles - clearing it at the headers
     * would leave a stalled body hanging forever. A timeout lands in the
     * catch like any other failure: the answer stays unknown, the render's
     * verdict stands, and whoever awaits this promise always gets it back.
     */
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var deadline = controller === null ? null : setTimeout(function () {
      controller.abort();
    }, READ_DEADLINE_MS);
    return fetch(base + '/products/' + encodeURIComponent(GIFT_HANDLE) + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal: controller === null ? undefined : controller.signal,
    })
      .then(function (res) { return (res.ok ? res.json() : null); })
      .then(function (product) {
        if (ticket <= stockApplied) return;
        if (!product || !Array.isArray(product.variants)) return;
        stockApplied = ticket;
        // Both directions, from the same answer: a size that sold out since
        // the render is closed, and one that came back is opened again. Only
        // disabling would let the offer reopen with nothing selectable in it.
        var live = {};
        var anyLeft = false;
        product.variants.forEach(function (v) {
          if (!v) return;
          var available = v.available === true;
          live[v.id] = available;
          // A variant added to the product after this page rendered has no
          // button here, so it cannot keep an offer alive: the dialog would
          // open with every size it can draw struck through.
          if (available && VARIANTS.indexOf(v.id) !== -1) anyLeft = true;
        });
        stockLearned = anyLeft;
        root.querySelectorAll('[data-club-gift-variant]').forEach(function (btn) {
          // A variant the shop no longer returns has been deleted, and a
          // button that outlives its variant can only fail when it is pressed.
          var available = live[parseInt(btn.dataset.clubGiftVariant, 10)] === true;
          if (available) {
            btn.removeAttribute('aria-disabled');
            btn.disabled = false;
            return;
          }
          btn.setAttribute('aria-disabled', 'true');
          btn.disabled = true;
        });
        if (!anyLeft) { setBanner(null); close(); }
      })
      .catch(function () {})
      .then(function () {
        if (deadline !== null) clearTimeout(deadline);
      });
  }
  var SPEND_MINOR = parseInt(root.dataset.spendMinor, 10) || 0;
  var CUSTOMER_ID = root.dataset.customerId || '';
  var PROMPT_KEY = 'club_gift_prompted_' + CUSTOMER_ID;
  var PROPERTY = root.dataset.property;
  var PROPERTY_VALUE = root.dataset.propertyValue;
  var RETURN_KEY = 'club_gift_return';
  var RETURN_PARAM = 'club_gift';
  // A return trip is worth honouring for one shopping session, not for a tab
  // left open overnight.
  var RETURN_MAX_AGE = 30 * 60 * 1000;
  var VARIANTS = (root.dataset.variants || '')
    .split(',')
    .map(function (v) { return parseInt(v, 10); })
    .filter(Boolean);
  var MIXED_ENDPOINT = safeMixedProxyEndpoint(
    root.dataset.mixedBridgeEndpoint || '',
    window.location.origin
  );
  var MIXED_CAMPAIGN = root.dataset.mixedBridgeCampaign || '';
  var MIXED_PRODUCT_ID = root.dataset.mixedGiftProductId || '';
  var MIXED_PROPERTY = root.dataset.mixedProperty || '_club_gift_mixed';
  var MIXED_CANARY = root.dataset.mixedBridgeCanary === 'true';
  var MIXED_MODE = root.dataset.mixedBridgeEnabled === 'true'
    && MIXED_ENDPOINT
    && /^[a-z0-9][a-z0-9_-]{0,63}$/.test(MIXED_CAMPAIGN)
    && /^\d+$/.test(MIXED_PRODUCT_ID)
    && PROPERTY
    && PROPERTY_VALUE
    && MIXED_PROPERTY
    ? 'canary'
    : 'off';
  // The floor rides the same signed endpoint and the same product echo; the
  // section's switch is the only thing that lets it be offered at all.
  var FLOOR_ENABLED = CAMPAIGN_ON
    && root.dataset.floorEnabled === 'true'
    && Boolean(MIXED_ENDPOINT)
    && /^\d+$/.test(MIXED_PRODUCT_ID);
  var FLOOR_EXPIRY_KEY = 'club_gift_floor_expiry_' + CUSTOMER_ID;
  var MIXED_ARMED = CAMPAIGN_ON && MIXED_MODE === 'canary' && MIXED_CANARY;
  // Either lane that hands out a short-lived code needs the restore and
  // expiry lifecycle, and the checkout gate that is re-armed with it.
  var LIFECYCLE_ARMED = MIXED_ARMED || FLOOR_ENABLED;
  // The gate holds a press only for a shopper whose cart can carry a code of
  // ours. A guest's press reaches the form untouched: the floor switch is
  // store-wide, her entitlement is not. A logged-in shopper is held whether
  // or not she is a member now: one who lost the tag may still carry a code
  // from before, and the Worker's refusal and the verified cleanup decide,
  // not the tag alone.
  // Armed for every logged-in shopper, switch or no switch: a code issued
  // before the floor was turned off may still sit in her cart, and the
  // landing cleanup that retires it is asynchronous. With the switch off the
  // gate only retires such a code; it asks the Worker for nothing.
  // A guest is held too, for the one thing her cart can carry: a code
  // claimed before she logged out. Her press is looked at once, the code
  // retired if there is one, and the Worker is never asked for her.
  var GATE_ARMED = true;
  // A benefit is live: the ways around the ordinary button are hidden.
  var GATE_LIVE = MIXED_ARMED || FLOOR_ENABLED;
  // One cleanup of a stale floor code per page load is followed by a fresh
  // evaluation; a second removal of the same cart would be a write loop, so
  // it stands the cleanup down until the page is loaded afresh.
  var floorCleanupPending = false;
  var floorCleanupStuck = false;

  var busy = false;
  var bannerState = null;
  var lastFocus = null;
  var offerReason = null;
  var offerLocked = false;
  // One reconciliation at a time, and one trailing work envelope holding
  // whatever arrived while it was working. Its obligations are separate so a
  // restore, a prompt-capable cart event and a drawer repaint can share one
  // turn without any one of them erasing the others.
  var syncRunning = null;
  var syncQueuedWork = null;
  // What the run in flight is carrying. Held separately because a run that is
  // cancelled never reaches the point where it would hand its own work on.
  var syncRunningWork = null;
  // Set only while the in-flight run's own cart read is still outstanding, and
  // cleared the moment that read is consumed.
  var syncReadController = null;
  // Raised when that read has been outstanding long enough to be treated as
  // stalled rather than merely slow.
  var syncReadOverdue = false;
  /*
   * How long the scheduler's own cart read may stay outstanding before it is
   * considered stalled. Deliberately far above any plausible /cart.js latency:
   * being wrong in one direction costs a single wasted read, while being too
   * eager restarts healthy reads on exactly the slow connections most of this
   * traffic uses - phones and the in-app browser. The attribute is not
   * rendered by the section and is absent in production; it exists so the
   * suite can exercise this in milliseconds instead of ten-second steps.
   */
  /*
   * Digits only, then a band. `parseInt` is forgiving in ways that matter
   * here: "1e9" reads as 1, "15e3" as 15, "20abc" as 20, "-5" as -5. Each of
   * those collapses the deadline to nothing - which is not a smaller safety
   * net but a different mechanism entirely: cancel-on-arrival, the one O08
   * refuted. A range check alone does not catch them, because the mantissa
   * can sit inside the range; the string has to be rejected, not coerced.
   *
   * The floor is a full second for the same reason. Anything shorter starts
   * cancelling reads that were about to answer.
   */
  var deadlineRaw = root.dataset.readDeadlineMs;
  var deadlineOverride = /^[0-9]+$/.test(String(deadlineRaw || '')) ? parseInt(deadlineRaw, 10) : NaN;
  var READ_DEADLINE_MS = (deadlineOverride >= 1000 && deadlineOverride <= 60000)
    ? deadlineOverride
    : 10000;
  // Set when the reconciler could not prove what happened to a marked line.
  // Nothing may re-offer, re-run or check out until the page loads afresh.
  var mixedLockedOut = false;

  function panel(name) {
    root.querySelectorAll('[data-club-gift-panel]').forEach(function (p) {
      p.hidden = p.dataset.clubGiftPanel !== name;
    });
  }

  function open(which) {
    if (!root.hidden) return;
    panel(which);
    lastFocus = document.activeElement;
    root.hidden = false;
    // The first control that is really there: on the floor road the garment
    // block and its link are rendered hidden, and focusing a hidden element
    // leaves focus on the page behind the dialog.
    var candidates = root.querySelectorAll(
      '[data-club-gift-panel]:not([hidden]) button:not([disabled]), [data-club-gift-panel]:not([hidden]) a'
    );
    var first = null;
    for (var i = 0; i < candidates.length && !first; i += 1) {
      if (!candidates[i].closest || !candidates[i].closest('[hidden]')) first = candidates[i];
    }
    if (first) first.focus();
  }

  function close() {
    if (root.hidden) return;
    root.hidden = true;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /*
   * Coming back from authentication is not a cart change, so nothing would
   * otherwise reopen the picker for a customer who left mid-decision and
   * returned entitled. The journey out leaves a mark; the journey back spends
   * it, once. The query parameter is the same signal for a redirect that
   * lands without the tab's storage.
   */
  function claimReturnMark() {
    var claimed = false;
    try {
      if (new URLSearchParams(window.location.search).get(RETURN_PARAM)) claimed = true;
    } catch (e) {}
    try {
      var raw = sessionStorage.getItem(RETURN_KEY);
      if (raw) {
        sessionStorage.removeItem(RETURN_KEY);
        var at = parseInt(raw, 10);
        if (at && Date.now() - at < RETURN_MAX_AGE) claimed = true;
      }
    } catch (e) {}
    return claimed;
  }

  function leaveReturnMark() {
    try { sessionStorage.setItem(RETURN_KEY, String(Date.now())); } catch (e) {}
  }

  /*
   * The once-ever mark for the accrual dialog, keyed by customer so a shared
   * device does not spend one customer's introduction on another. When storage
   * cannot be read the mark counts as spent: a dialog that cannot promise
   * "once" must not fire at all - the banner still carries the offer.
   */
  function hasBeenPrompted() {
    if (!CUSTOMER_ID) return true;
    try { return window.localStorage.getItem(PROMPT_KEY) === '1'; }
    catch (e) { return true; }
  }

  function markPrompted() {
    if (!CUSTOMER_ID) return;
    try { window.localStorage.setItem(PROMPT_KEY, '1'); } catch (e) {}
  }

  // The base theme owns the cart UI, so refreshing means handing it the
  // response and letting it re-render its own sections.
  function cartRequest(url, payload) {
    var miniCart = document.querySelector('mini-cart');
    payload.sections = miniCart
      ? miniCart.getSectionsToRender().map(function (s) { return s.section; })
      : [];
    payload.sections_url = window.location.pathname;
    return fetch(url, Object.assign({}, fetchConfig(), { body: JSON.stringify(payload) }))
      .then(function (res) {
        // A refused write (422, 429, 5xx) is a failure, not a response to
        // render or reload on: on the cart template a reload would find the
        // same cart and write again, for as long as the refusal lasts.
        if (!res.ok) throw new Error('cart write refused: ' + res.status);
        return res.json();
      })
      .then(function (response) {
        if (document.body.classList.contains('template-cart')) {
          window.location.href = theme.routes.cart_url;
          return response;
        }
        if (miniCart) miniCart.renderContents(response);
        return response;
      });
  }

  function addGift(variantId) {
    var item = { id: variantId, quantity: 1, properties: {} };
    // Underscore-prefixed properties stay off the customer-facing line. This
    // is an audit breadcrumb; the variant in the cart is the size.
    item.properties[PROPERTY] = PROPERTY_VALUE;
    return cartRequest(theme.routes.cart_add_url, { items: [item] });
  }

  function removeAllGiftLines() {
    var updates = {};
    VARIANTS.forEach(function (id) { updates[id] = 0; });
    return cartRequest(theme.routes.cart_update_url, { updates: updates });
  }

  function setGiftQuantity(variantId, quantity) {
    var updates = {};
    updates[variantId] = quantity;
    return cartRequest(theme.routes.cart_update_url, { updates: updates });
  }

  function readCart(signal) {
    var base = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/')
      .replace(/\/$/, '');
    // Every verdict in this file rests on this one response, so it must never
    // come from a store. A tab restored after half an hour was handed the
    // pre-expiry body: the cart read as free and checkout charged for the gift.
    var options = {
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
      cache: 'no-store',
    };
    /*
     * Only the scheduler passes one, and only for its own read. The claim,
     * checkout and bridge reads stay uncancellable because each is a step in a
     * decision that is about to move money, and abandoning one half-done is
     * worse than waiting.
     *
     * Not because nothing waits on them - an earlier version of this comment
     * said that and it was wrong. The claim read runs with `busy` held, and a
     * turn already in the queue waits for `releaseBusy` to drain it, so a claim
     * read that stalls does strand that turn. It does so identically on
     * `25c2684`, which is why it is left alone here rather than quietly
     * widened into: it is a pre-existing gap, recorded, not this fix's to take.
     */
    if (signal) options.signal = signal;
    // Stamped before the request leaves: a cart change announced while this
    // body streams makes the answer old, and a proof must answer for the
    // world it was read under, not for the moment someone paints with it.
    var provedAt = cartGeneration;
    return fetch(base + '/cart.js', options)
      .then(function (res) { return (res.ok ? res.json() : null); })
      .then(function (parsed) {
        if (parsed && typeof parsed === 'object') {
          try {
            Object.defineProperty(parsed, 'provedAtGeneration', { value: provedAt });
          } catch (e) {
            parsed.provedAtGeneration = provedAt;
          }
        }
        return parsed;
      });
  }

  function cartJsonMutation(url, payload) {
    return fetch(url, Object.assign({}, fetchConfig(), { body: JSON.stringify(payload) }))
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok || (json && json.status && Number(json.status) >= 400)) {
            throw new Error('cart-mutation-failed');
          }
          return json;
        });
      });
  }

  function requestMixedGrant(request) {
    var body = new URLSearchParams();
    Object.keys(request).forEach(function (key) { body.set(key, request[key]); });
    return fetch(MIXED_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body: body.toString(),
    }).then(function (res) {
      if (!res.ok) throw new Error('grant-failed');
      return res.json();
    });
  }

  // A campaign that already minted grants can never be redefined, so a new
  // window means a new campaign key. Carts from the previous one may still be
  // sitting in a browser somewhere: their line is recognised so it can be
  // cleaned up, and it is never granted against the current campaign.
  var LEGACY_MIXED_CAMPAIGNS = [
    'welcome-2026-stage0',
    'welcome-2026-repair-stage1',
    'welcome-2026-public-20260808',
  ];

  // The retired campaigns' gift product (sweet-dreams-pointelle-set). The
  // section renders only the current product's variants, so a bridge line of
  // the old one left in a returning cart would otherwise never be recognised
  // and never cleaned. These IDs exist for that recognition alone: they must
  // not join VARIANTS, own native gifts or fund the qualifying sum.
  var LEGACY_GIFT_VARIANTS = [48502501638389, 48502501671157, 48502501703925];

  var MIXED_CONFIG = {
    variantIds: VARIANTS,
    basePropertyName: PROPERTY,
    basePropertyValue: PROPERTY_VALUE,
    mixedPropertyName: MIXED_PROPERTY,
    campaign: MIXED_CAMPAIGN,
    legacyCampaigns: LEGACY_MIXED_CAMPAIGNS.filter(function (key) {
      return key !== MIXED_CAMPAIGN;
    }),
    legacyVariantIds: LEGACY_GIFT_VARIANTS,
  };

  function addMixedGift(variantId, properties) {
    return cartJsonMutation(theme.routes.cart_add_url, {
      items: [{ id: variantId, quantity: 1, properties: properties }],
    });
  }

  /*
   * The full observed list plus ours. Sending a single code would replace
   * every code the shopper had entered.
   */
  function applyMixedDiscountCodes(codes) {
    return cartJsonMutation(theme.routes.cart_update_url, { discount: codes });
  }

  /*
   * The floor's writes go through the base theme's own cart refresh: an
   * order-level code changes the totals the drawer and the cart page show,
   * and a strip saying the gift is active over stale totals would read as a
   * lie. On the cart template this reloads the page, exactly as the native
   * gift add does, and the landing reconcile then re-proves the code.
   */
  function applyFloorDiscountCodes(codes) {
    return cartRequest(theme.routes.cart_update_url, { discount: codes });
  }

  function removeMixedLine(key) {
    return cartJsonMutation(theme.routes.cart_change_url, { id: key, quantity: 0 });
  }

  /*
   * The verdict comes from a fresh /cart.js read, never from the mutation
   * response, so there are no sections in hand once the line is settled. The
   * drawer is asked to repaint from the Section Rendering API instead: a
   * reload would work but throws away her scroll position and, on a restore,
   * fires the whole lifecycle again.
   */
  // Advanced on every announced cart change; a converge started against an
  // older value stands down instead of painting over newer truth.
  var cartGeneration = 0;

  function renderCurrentMixedCart(cart) {
    // The proven cart rides along so the repaint can refuse stale section
    // markup; without it the convergence check has nothing to converge to.
    // The proof's own stamp wins over the current counter: a bump that landed
    // while the cart body was streaming already outdates this cart. The stamp
    // is non-enumerable and does not survive a copy - if a future edit hands
    // a spread of the cart down this path, the counter fallback quietly
    // reopens the streaming-read race instead of failing loudly.
    var startedAt = cart && typeof cart.provedAtGeneration === 'number'
      ? cart.provedAtGeneration
      : cartGeneration;
    return renderMixedCart({
      documentTarget: document,
      windowTarget: window,
      fetchFn: fetch,
      cartUrl: theme.routes.cart_url,
      cart: cart,
      config: MIXED_CONFIG,
      stillCurrent: function () { return cartGeneration === startedAt; },
    });
  }

  /*
   * When our code dies, kept per customer and campaign so a shared device
   * cannot lend one shopper's window to another and a campaign change starts
   * clean. Storage that cannot be read leaves the expiry unknown, which falls
   * back to the cart rather than buying a grant on every page view.
   */
  var MIXED_EXPIRY_KEY = 'club_gift_mixed_expiry_' + MIXED_CAMPAIGN + '_' + CUSTOMER_ID;

  function rememberMixedGrant(expiresAt) {
    if (!CUSTOMER_ID || !expiresAt) return;
    try { window.localStorage.setItem(MIXED_EXPIRY_KEY, String(expiresAt)); } catch (e) {}
    // The lifecycle hears about every expiry the runtime learns, so a
    // visible idle tab re-evaluates when the code dies instead of waiting
    // for the shopper to touch the cart.
    if (mixedLifecycle) mixedLifecycle.watchExpiry(expiresAt);
  }

  function recalledMixedExpiry() {
    if (!CUSTOMER_ID) return null;
    try { return window.localStorage.getItem(MIXED_EXPIRY_KEY); } catch (e) { return null; }
  }

  var MIXED_DEPS = {
    requestGrant: requestMixedGrant,
    readCart: function () { return readCart(); },
    addGift: addMixedGift,
    applyCodes: applyMixedDiscountCodes,
    removeLine: removeMixedLine,
    // The native lane's own removal, lent to the gate so a charged gift comes
    // off there too. The bridge never calls this for a gift that is free.
    removeNativeGift: function () { return removeAllGiftLines(); },
    renderCart: renderCurrentMixedCart,
    rememberGrant: rememberMixedGrant,
  };

  function mixedInputFor(cart, intent, variantId) {
    return {
      cart: cart,
      grantExpiresAt: recalledMixedExpiry(),
      nowMs: Date.now(),
      config: MIXED_CONFIG,
      intent: intent,
      variantId: variantId,
      mode: MIXED_MODE,
      isCanary: MIXED_CANARY,
      thresholdMinor: THRESHOLD,
      isLoggedIn: IS_LOGGED_IN,
      isMember: IS_MEMBER,
      hasRedeemed: HAS_REDEEMED,
      spendMinor: SPEND_MINOR,
      giftAvailable: giftAvailableNow(),
      giftProductId: MIXED_PRODUCT_ID,
    };
  }

  /*
   * The single owner of the marked line. Everything else - banner, checkout
   * gate, lifecycle - only reads its typed outcome.
   */
  function reconcileMixed(cart, intent, variantId) {
    return reconcileMixedGift(mixedInputFor(cart, intent, variantId), MIXED_DEPS)
      .then(function (result) {
        var outcome = result && result.outcome;
        if (outcome === 'verified_zero') {
          offerReason = null;
          mixedLockedOut = false;
          setBanner('claimed');
        } else if (outcome === 'removed_ineligible' || outcome === 'removed_unverified') {
          offerReason = null;
          mixedLockedOut = false;
          setBanner('error');
        } else if (outcome === 'blocked') {
          offerReason = null;
          mixedLockedOut = true;
          // Nothing was removed here, so the removal message would be a lie.
          // The conflict has a message of its own that names what she can fix.
          setBanner(result.reason === 'ambiguous-gift-lines' ? 'conflict' : 'error');
        }
        return result || { outcome: 'not_applicable' };
      })
      .catch(function () {
        mixedLockedOut = true;
        return { outcome: 'blocked', checkoutLocked: true };
      });
  }

  /*
   * Everything that is not the size being unavailable. Telling her to try
   * another size when the bridge never answered sends her through all three
   * before she gives up, and blames the stock for something else entirely.
   */
  var MIXED_SYSTEM_REASONS = [
    'grant-denied', 'cart-unavailable', 'unverifiable', 'read-failed',
    'opaque-discounts', 'gap-mismatch', 'still-charged', 'marker-missing',
    'ambiguous-gift-lines', 'suspicious-marker', 'duplicate-marker',
  ];

  /*
   * Both roads end here when a size does not make it into the cart, and the
   * mixed road swallows the failed add on its way - so the shelf is re-asked
   * from the one place both of them pass through.
   */
  function showClaimError(reason) {
    // The shelf is re-asked, and the answer is acted on: a size that just
    // went may have emptied the shelf, and the floor is then the benefit she
    // is owed. Queued, not called: the claim still holds `busy` here, and
    // its release drains the queue.
    refreshGiftAvailability().then(function () {
      syncQueuedWork = mergeSyncWork(syncQueuedWork, 'cart-change');
      drainQueuedSync();
    });
    var which = MIXED_SYSTEM_REASONS.indexOf(String(reason || '')) === -1 ? 'size' : 'system';
    root.querySelectorAll('[data-club-gift-error]').forEach(function (el) {
      el.hidden = el.dataset.clubGiftError !== which;
    });
  }

  function claimMixedVariant(variantId) {
    root.querySelectorAll('[data-club-gift-error]').forEach(function (el) { el.hidden = true; });
    busy = true;
    root.querySelectorAll('[data-club-gift-variant]').forEach(function (b) { b.disabled = true; });

    readCart()
      .then(function (cart) {
        if (!cart) throw new Error('cart-unavailable');
        return reconcileMixed(cart, 'claim', variantId);
      })
      .then(function (result) {
        if (result.outcome === 'verified_zero') {
          close();
          return;
        }
        showClaimError(result.reason);
      })
      .catch(function () { showClaimError('cart-unavailable'); })
      .finally(function () {
        releaseBusy();
        root.querySelectorAll('[data-club-gift-variant]').forEach(function (b) {
          b.disabled = b.hasAttribute('aria-disabled');
        });
      });
  }

  /*
   * The floor's own grant call. Unlike the mixed road, a refusal carries a
   * reason the storefront acts on: a size back in stock sends her to the gift
   * road rather than to an error.
   */
  function requestFloorGrant(intent) {
    var body = new URLSearchParams();
    body.set('action', 'grant');
    body.set('benefit', 'floor');
    body.set('gift_product_id', MIXED_PRODUCT_ID);
    if (intent === 'checkout') body.set('intent', 'checkout');
    return fetch(MIXED_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body: body.toString(),
    }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (json) {
        return { status: res.status, body: json };
      });
    });
  }

  /*
   * The grant's expiry and percent, kept per customer so the confirmation
   * strip can quote the number on a later page view without a second grant.
   * Storage that cannot be read leaves the number blank, never invented.
   */
  function rememberFloorGrant(expiresAt, percent) {
    if (!CUSTOMER_ID || !expiresAt) return;
    try {
      window.localStorage.setItem(FLOOR_EXPIRY_KEY, JSON.stringify({ expiresAt: String(expiresAt), percent: percent }));
    } catch (e) {}
    if (mixedLifecycle) mixedLifecycle.watchExpiry(expiresAt);
  }

  function recalledFloorGrant() {
    if (!CUSTOMER_ID) return null;
    try {
      var raw = JSON.parse(window.localStorage.getItem(FLOOR_EXPIRY_KEY) || 'null');
      if (!raw || typeof raw !== 'object') return null;
      var percent = raw.percent;
      if (typeof percent !== 'number' || percent < 1 || percent > 100) percent = null;
      return { expiresAt: raw.expiresAt || null, percent: percent };
    } catch (e) { return null; }
  }

  /*
   * Grant, apply, prove. The code goes into the cart next to every code she
   * already had, and only a fresh read that shows it applicable closes the
   * dialog. A refusal because a size came back re-asks the shelf and lets
   * the next turn offer the gift instead.
   */
  function claimFloor() {
    root.querySelectorAll('[data-club-gift-error]').forEach(function (el) { el.hidden = true; });
    var buttons = root.querySelectorAll('[data-club-gift-floor-claim]');
    busy = true;
    buttons.forEach(function (b) { b.disabled = true; });
    var granted = null;
    requestFloorGrant('claim')
      .then(function (answer) {
        if (answer.status === 409 && answer.body && answer.body.reason === 'gift_in_stock') {
          throw new Error('gift-in-stock');
        }
        granted = checkedFloorGrant(answer.body);
        if (!granted) throw new Error('grant-denied');
        // Remembered before the write: on the cart template the write reloads
        // the page, and the landing reconcile needs the number on record.
        rememberFloorGrant(granted.expiresAt, granted.percent);
        return readCart();
      })
      .then(function (cart) {
        if (!cart || !cartCodesReadable(cart)) throw new Error('cart-unavailable');
        return applyFloorDiscountCodes(mergeDiscountCodes(cartExplicitCodes(cart), granted.code));
      })
      .then(function () { return readCart(); })
      .then(function (cart) {
        if (floorCodesIn(cart).length !== 1 || !floorApplied(cart, granted.code)) throw new Error('not-applied');
        // A gift line that arrived meanwhile (another tab) wins: the floor is
        // not confirmed over a cart holding both, and the code comes off again
        // through the same cleanup as a code the cart would not honour.
        if (floorBesideGift(cart)) throw new Error('not-applied');
        offerReason = null;
        setBanner('floor-claimed');
        close();
      })
      .catch(function (err) {
        var reason = err && err.message;
        if (reason === 'gift-in-stock') {
          close();
          refreshGiftAvailability().then(function () { requestReconcile('cart-change'); });
          return;
        }
        root.querySelectorAll('[data-club-gift-error]').forEach(function (el) {
          el.hidden = el.dataset.clubGiftError !== 'floor';
        });
        if (reason === 'not-applied') {
          // Shopify kept her own code instead (D3: the floor yields). The
          // stray is taken off again so checkout never meets a dead code.
          // Returned, so the claim stays busy until the write has landed: a
          // retry that ran meanwhile would be erased by this cleanup's list.
          return readCart().then(function (cart) {
            var verdict = floorCodeVerdict(cart, { action: 'none', reason: 'yielded' }, false);
            if (verdict.action !== 'remove') return undefined;
            return applyFloorDiscountCodes(verdict.remaining).then(function () {
              // Off the cart template the write only repaints the drawer, and
              // that request is refused while the claim holds `busy`; a turn
              // is queued for the release, so whatever the clean cart now
              // holds (a gift that arrived meanwhile) is rendered at once.
              syncQueuedWork = mergeSyncWork(syncQueuedWork, 'cart-change');
            });
          }).catch(function () {});
        }
        return undefined;
      })
      .finally(function () {
        buttons.forEach(function (b) { b.disabled = false; });
        releaseBusy();
      });
  }

  // The banner is the standing offer; the dialog is only how a size gets
  // chosen. Every cart surface re-renders on change, so the strips are found
  // fresh each time rather than held in a variable that would go stale.
  function paintBanner() {
    document.querySelectorAll('[data-club-gift-banner]').forEach(function (el) {
      el.hidden = bannerState === null;
      if (bannerState) el.dataset.state = bannerState;
      el.querySelectorAll('[data-club-gift-banner-state]').forEach(function (part) {
        part.hidden = part.dataset.clubGiftBannerState !== bannerState;
      });
    });
    // Re-rendered strips arrive without the number; it is repainted from the
    // remembered grant every time the confirmation is shown.
    if (bannerState === 'floor-claimed') {
      var recalled = recalledFloorGrant();
      paintFloorPercent(recalled ? recalled.percent : null);
    }
  }

  // null hides the strip entirely; 'offer' invites; 'claimed' confirms.
  function setBanner(state) {
    bannerState = state;
    paintBanner();
  }

  function paintMemberRoad(reason) {
    var road = reason === 'entitled-mixed' ? 'mixed' : isFloorReason(reason) ? 'floor' : 'native';
    root.querySelectorAll('[data-club-gift-road]').forEach(function (element) {
      element.hidden = element.dataset.clubGiftRoad !== road;
    });
    // The garment and its sizes have no place on the floor road: the benefit
    // is a percentage, and naming an item would promise one.
    root.querySelectorAll('[data-club-gift-item]').forEach(function (element) {
      element.hidden = road === 'floor';
    });
  }

  // The number is the Worker's; without one on record the line reads without
  // it rather than with a blank in front of the percent sign.
  // Written only when it changes: the slot sits inside the drawer, which is
  // watched for content swaps, and replacing a text node with the same text
  // is a content swap. Writing on every paint fed that observer, which
  // repainted, which wrote: a microtask loop that froze the page after the
  // first live claim from the drawer.
  function paintFloorPercent(percent) {
    var text = typeof percent === 'number' ? String(percent) + '% ' : '';
    document.querySelectorAll('[data-club-gift-floor-percent]').forEach(function (el) {
      if (el.textContent !== text) el.textContent = text;
    });
  }

  function act(decision, mayOpen) {
    offerLocked = offerLockout(offerLocked, decision);
    if (decision.action === 'remove-gift') {
      offerReason = null;
      setBanner(null);
      busy = true;
      // The native lane keeps its existing bulk update and swallowed error
      // policy. A marked line never reaches here: the reconciler owns it and
      // returns before the native decision runs.
      return removeAllGiftLines()
        .catch(function () {})
        .finally(releaseBusy);
    }
    if (decision.action === 'fix-quantity') {
      offerReason = null;
      setBanner(null);
      busy = true;
      return setGiftQuantity(decision.variant, 1)
        .catch(function () {})
        .finally(releaseBusy);
    }
    if (decision.action === 'ask-member') {
      if (offerLocked) {
        offerReason = null;
        setBanner(null);
        return undefined;
      }
      offerReason = decision.reason;
      paintMemberRoad(decision.reason);
      // Shown whether or not the dialog opens. This is the path a customer
      // lands on after leaving to authenticate, and after dismissing the
      // dialog - both of which used to end in a gift she was owed and could
      // not reach.
      setBanner(isFloorReason(decision.reason) ? 'floor-offer' : 'offer');
      if (mayOpen) return open('member');
      return undefined;
    }
    offerReason = null;
    // 'settled' is the one non-ask state that still owns the strip: the gift is
    // in the cart and she should be told, not left to infer it from silence.
    setBanner(decision.reason === 'settled' ? 'claimed'
      : decision.reason === 'settled-floor' ? 'floor-claimed' : null);
    if (decision.action === 'ask-join' && mayOpen) return open('join');
    if (decision.action === 'ask-account' && mayOpen) return open('account');
    if (decision.action === 'none') close();
    return undefined;
  }

  /*
   * The only scheduler. Lifecycle events delegate here directly, so an armed
   * drawer echo cannot hide in a second queue that bypasses `busy`. Two
   * reconciliations never overlap, and every arrival during one is merged into
   * a single trailing envelope.
   *
   * The `busy` test remains first and load-bearing. A native write repaints the
   * drawer synchronously while it owns `busy`; dropping that self echo bounds
   * the write to its initiating event. No event-name suppression is used, so a
   * later user event still reaches the scheduler once ownership is released.
   */
  function sync(value) {
    var work = syncWorkFor(value);
    /*
     * The refusal is the ownership bound itself and stays wholesale for
     * everything a write can echo back. One obligation survives it: the
     * shelf refresh a restore carries, because it answers a question no cart
     * read or drawer repaint asks, and an echo never carries it. What is
     * retained is a bare restore - no prompt permission, no drawer work, the
     * lowest-rank reason - so draining it can neither open a dialog nor feed
     * the echo back, and merging it twice is the same as merging it once.
     */
    if (busy) {
      if (work.restoreNeeded) {
        syncQueuedWork = mergeSyncWork(syncQueuedWork, {
          fallbackReason: 'restore',
          restoreNeeded: true,
          promptReason: null,
          drawerNeeded: false,
        });
      }
      return Promise.resolve();
    }
    if (syncRunning) {
      syncQueuedWork = mergeSyncWork(syncQueuedWork, work);
      // Without this the queue is only as reliable as the network: a read that
      // never answers holds the turn forever and nothing behind it ever runs.
      cancelStalledSyncRead();
      return syncRunning;
    }
    syncRunningWork = work;
    syncRunning = runSync(work)
      // The run reports nothing upwards, so a failure inside it must not strand
      // the turn: the queue is drained from a handler that a rejection reaches.
      .catch(function () {})
      .then(function () {
        syncRunning = null;
        syncRunningWork = null;
        return drainQueuedSync();
      });
    return syncRunning;
  }

  /*
   * A write started outside a run - she picked a size - holds the drain. The
   * turn stays in the queue rather than being spent on a call that would be
   * refused, and `releaseBusy` runs it as soon as the write is done, so it
   * never waits on a cart event that may not come.
   */
  function drainQueuedSync() {
    if (syncRunning || busy || syncQueuedWork === null) return undefined;
    var next = syncQueuedWork;
    syncQueuedWork = null;
    // The queued run is a revalidation like any other: it starts from not
    // knowing, and only its own clean read reveals the controls.
    acceleratedCartState = 'unknown';
    hideAcceleratedCheckout();
    return sync(next);
  }

  /*
   * The one way `busy` is cleared outside a reconciliation. Nothing our own
   * writes echoed back is in the queue - those are refused at the top of
   * `sync` and never reach it. The busy gate does retain a bare restore, but
   * a restore is never an echo of ours, and its turn ends with the queue
   * empty - so draining here cannot feed a write loop.
   */
  function releaseBusy() {
    busy = false;
    drainQueuedSync();
  }

  /*
   * This run read the cart before a newer event arrived, so its answer is
   * known to be behind. Painting or writing from it would act on a cart that
   * has already moved. The turn is folded into the queue instead - keeping
   * whichever reason is allowed to open the dialog - and the trailing run
   * decides from a fresh read.
   */
  function supersededByQueuedWork(work) {
    if (syncQueuedWork === null) return false;
    // Every unanswered obligation the active turn carried survives alongside
    // the newer arrival. Restore work already attempted by this run is cleared
    // before the read starts, so it is not paid twice here.
    syncQueuedWork = mergeSyncWork(syncQueuedWork, work);
    return true;
  }

  /*
   * A cart read has no deadline of its own. A rejected one is fine - it lands
   * in the catch below and the queue drains - but a stalled connection gives a
   * promise that never settles at all, reaches no handler ever, and leaves
   * every later arrival waiting behind it for as long as the tab is open.
   *
   * Two conditions, and both are load-bearing.
   *
   * Overdue, because cancelling a read merely because something arrived behind
   * it would restart healthy reads: a single add produces several drawer
   * repaints, and each one would throw away a read that was about to answer.
   *
   * Something waiting, because a stalled read with an empty queue harms
   * nobody - and cancelling it would start a fresh read that stalls the same
   * way, and another, for as long as the connection stays bad. With the guard,
   * a stall costs exactly one retry per genuinely waiting turn.
   *
   * `abort()` really cancels the request rather than ignoring its answer, which
   * matters: a raced-but-live read could still come back later and act on a
   * cart that has since moved, which is the defect the freshness gate exists to
   * prevent. The rejection is an ordinary one, so it travels the path that
   * already exists - catch, drain, then a fresh run from a fresh read.
   */
  function cancelStalledSyncRead() {
    if (syncReadController === null || syncReadOverdue !== true) return;
    if (syncQueuedWork === null) return;
    var controller = syncReadController;
    syncReadController = null;
    syncReadOverdue = false;
    /*
     * Hand the cancelled run's work on before killing it. It never reaches
     * `supersededByQueuedWork`, which is what normally folds a deferred run's
     * work back into the queue. Without this a cancelled cart change could
     * leave only a drawer repaint behind and silently lose prompt permission.
     */
    syncQueuedWork = mergeSyncWork(syncQueuedWork, syncRunningWork);
    controller.abort();
  }

  /*
   * One benefit at a time. A cart can come to hold an honoured floor code
   * beside a marked gift line (two tabs, a size that came back): the gift is
   * the one in the cart, and the Worker revokes the floor when it grants the
   * gift, so the code is what leaves.
   */
  function floorBesideGift(cart) {
    // Not gated on the switch: a code issued before the floor was turned
    // off is still a second benefit beside a gift line.
    if (!cart) return false;
    if (floorCodesIn(cart).length === 0) return false;
    // Any gift line of ours, native or marked: both ride the gift variants.
    if (measureCart(cart, VARIANTS).giftLines > 0) return true;
    return MIXED_ARMED && measureMixedMarker(cart, MIXED_CONFIG).markerLines.length > 0;
  }

  function runSync(work) {
    // Shelf freshness is part of the same turn as the cart verdict. Once it
    // has been attempted, clear only that obligation; prompt permission and
    // drawer convergence remain attached to the run and survive cancellation.
    var refreshed = work.restoreNeeded ? refreshGiftAvailability() : Promise.resolve();
    return refreshed.then(function () {
      /*
       * The refresh takes real network time, and a size pick can take `busy`
       * while it runs. Reading now would reconcile a snapshot from before
       * that write, concurrently with it - the overlap the head gate exists
       * to refuse - so the same refusal applies at this seam. Nothing is
       * retained: the refresh, the one obligation that survives a refusal,
       * has just been paid; prompt permission never outlives a refusal; and
       * the write that owns `busy` repaints the drawer from its own
       * response. Work already queued behind this run is untouched and
       * drains when ownership is released. Guarded on `restoreNeeded` so a
       * turn that never awaited a refresh keeps the head gate as its only
       * busy test.
       */
      if (work.restoreNeeded && busy) return undefined;
      var activeWork = {
        fallbackReason: work.fallbackReason,
        restoreNeeded: false,
        promptReason: work.promptReason,
        drawerNeeded: work.drawerNeeded,
      };
      syncRunningWork = activeWork;
      var trigger = triggerForSyncWork(activeWork);

      /*
       * The read is cancellable only until it has been consumed. Per the fetch
       * contract an abort after the response arrives but before its body is
       * read rejects `json()`, and one after that does nothing at all - so
       * ownership is released the moment the cart is in hand, and never held
       * across the decision that follows.
       */
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      syncReadController = controller;
      syncReadOverdue = false;
      var deadline = controller === null ? null : setTimeout(function () {
        if (syncReadController !== controller) return;
        syncReadOverdue = true;
        cancelStalledSyncRead();
      }, READ_DEADLINE_MS);
      function releaseReadControl() {
        if (deadline !== null) clearTimeout(deadline);
        // Compared by identity, not truthiness: a run finishing late must never
        // clear the controller a newer run has since installed.
        if (syncReadController !== controller) return;
        syncReadController = null;
        syncReadOverdue = false;
      }
      return readCart(controller ? controller.signal : null)
        .then(function (cart) {
          releaseReadControl();
          if (!cart) return undefined;
          // Only a snapshot nothing newer has superseded may speak for the
          // accelerated controls: a stale clean read must not reveal them
          // while the queued run's fresh read is still owed.
          if (syncQueuedWork === null) hideAcceleratedCheckout(cart);
          // A snapshot a newer cart event has superseded writes nothing: the
          // queued run decides from its own fresh read, so a gift that has
          // since come off does not cost her the code, and a list another
          // update has since changed is not rewritten from the old picture.
          if (supersededByQueuedWork(activeWork)) return undefined;
          // A read that lists no floor code closes any cleanup in flight,
          // whichever lane answers this turn; a code that appears later is a
          // new stray, not the one that would not leave.
          if (floorCodesIn(cart).length === 0) { floorCleanupPending = false; floorCleanupStuck = false; }
          // A floor code beside a marked gift line leaves before either lane
          // is judged; the drain re-evaluates the clean cart. A code list
          // that cannot be read faithfully is left alone: a rewrite would
          // wipe codes it could not see.
          var besideVerdict = floorBesideGift(cart) && !floorCleanupStuck
            ? floorCodeVerdict(cart, { action: 'none', reason: 'gift-present' }, true)
            : null;
          if (besideVerdict && besideVerdict.action === 'remove') {
            if (floorCleanupPending) { floorCleanupStuck = true; return undefined; }
            busy = true;
            return applyFloorDiscountCodes(besideVerdict.remaining)
              .then(function () {
                floorCleanupPending = true;
                syncQueuedWork = mergeSyncWork(syncQueuedWork, 'reconcile');
                releaseBusy();
              }, function () { releaseBusy(); });
          }
          // The reconciler is asked first and, whenever it owns a marked line,
          // it answers alone. Only a cart with no line of ours falls through
          // to the unchanged native decision below.
          return reconcileMixed(cart, 'reconcile', 0).then(function (result) {
            if (supersededByQueuedWork(activeWork)) return undefined;
            if (result.outcome !== 'not_applicable') {
              // An older rapid-add response can repaint stale drawer markup
              // after the grant's first repaint. The drawer MutationObserver
              // routes that overwrite here; if the fresh cart proves a
              // settled gift but the mini-cart alone lacks its row, converge
              // once more. A coalesced drawer obligation survives regardless
              // of which fallback reason labels the turn.
              var miniCart = document.querySelector('mini-cart');
              if (activeWork.drawerNeeded
                  && result.outcome === 'verified_zero'
                  && mixedDrawerNeedsRepaint(cart, MIXED_CONFIG, miniCart)) {
                return renderCurrentMixedCart(cart);
              }
              return undefined;
            }
            return nativeSync(cart, trigger);
          });
        })
        .catch(function () { releaseReadControl(); });
    });
  }

  function nativeSync(cart, trigger) {
    var measured = measureCart(cart, VARIANTS);
    // With the campaign off there is no decision to make, only a code from
    // before to retire: a 'none' decision makes any floor code a stray.
    var nativeDecision = CAMPAIGN_ON ? decideGift(measured, {
      thresholdMinor: THRESHOLD,
      isMember: IS_MEMBER,
      isLoggedIn: IS_LOGGED_IN,
      hasRedeemed: HAS_REDEEMED,
      spendMinor: SPEND_MINOR,
      giftAvailable: giftAvailableNow(),
      floorEnabled: FLOOR_ENABLED,
    }) : { action: 'none', reason: 'campaign-off' };
    var decision = nativeDecision;
    /*
     * A floor code already in the cart is answered before anything is
     * offered. Kept, it is the settled state and the strip confirms it;
     * stray - a size back on the shelf, a cart under the line - it comes off
     * first, her other codes rewritten intact, so the gift road never meets
     * a cart that still carries the percentage.
     */
    // The remembered clock is evidence too: Shopify can keep listing a dead
    // code as applicable for a while, and a code whose grant has expired is
    // a stray whatever the listing says. Judged as if a size were back.
    var recalledFloor = recalledFloorGrant();
    var floorExpired = Boolean(recalledFloor && recalledFloor.expiresAt
      && mixedGrantExpired(recalledFloor.expiresAt, Date.now(), 0));
    var floorCodes = floorCodeVerdict(cart, nativeDecision, floorExpired ? true : giftAvailableNow());
    if (floorCodes.action === 'remove') {
      if (floorCleanupStuck) return undefined;
      if (floorCleanupPending) {
        // The write landed and the code is still listed: nothing of ours
        // will change that from here. Stop writing.
        floorCleanupStuck = true;
        return undefined;
      }
      offerReason = null;
      setBanner(null);
      busy = true;
      return applyFloorDiscountCodes(floorCodes.remaining)
        .then(function () {
          floorCleanupPending = true;
          // Off the cart template the write only repaints the drawer, and the
          // repaint's request is refused while `busy` holds. A fresh turn is
          // queued for the drain that ends this run, so the clean cart is
          // evaluated at once and the offer the removal made room for shows.
          // Queued, not called: a call from inside the run would wait on the
          // run itself.
          syncQueuedWork = mergeSyncWork(syncQueuedWork, 'reconcile');
          releaseBusy();
        }, function () {
          releaseBusy();
        });
    }
    floorCleanupPending = false;
    if (!CAMPAIGN_ON) return undefined;
    if (floorCodes.action === 'keep') {
      decision = { action: 'none', reason: 'settled-floor' };
    }
    // The mixed offer is additive and narrow: it only ever turns the native
    // below-threshold silence into an invitation, and only for a customer the
    // money rules already accept.
    if (nativeDecision.action === 'none'
        && nativeDecision.reason === 'below-threshold'
        && MIXED_MODE === 'canary'
        && MIXED_CANARY) {
      var marker = measureMixedMarker(cart, MIXED_CONFIG);
      var mixed = decideMixedGift(marker, mixedInputFor(cart, 'reconcile', 0));
      if (mixed.eligible
          && mixed.state === 'no_marker'
          && marker.unmarkedGiftLines === 0
          && marker.codesReadable) {
        decision = { action: 'ask-member', reason: 'entitled-mixed' };
      }
    }
    var prompt = decidePrompt(decision, {
      trigger: trigger,
      promptedBefore: hasBeenPrompted(),
    });
    // A locked offer will not open a dialog, so the once-ever moment is not
    // spent on one that cannot appear.
    if (prompt.remember && !offerLocked) markPrompted();
    return act(decision, prompt.open);
  }

  root.querySelectorAll('[data-club-gift-dismiss]').forEach(function (el) {
    el.addEventListener('click', close);
  });
  root.querySelectorAll('form[action*="join-intent"]').forEach(function (form) {
    form.addEventListener('submit', function () {
      leaveReturnMark();
      try {
        sessionStorage.setItem('club_join_intent', String(Date.now()));
        sessionStorage.setItem('return_after_login', JSON.stringify({ p: '/pages/club', t: Date.now() }));
      } catch (e) {}
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !root.hidden) close();
  });

  root.querySelectorAll('[data-club-gift-floor-claim]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (busy || btn.disabled || !FLOOR_ENABLED) return;
      if (!isFloorReason(offerReason)) return;
      claimFloor();
    });
  });

  root.querySelectorAll('[data-club-gift-variant]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (busy || btn.disabled) return;
      var variantId = parseInt(btn.dataset.clubGiftVariant, 10);
      if (VARIANTS.indexOf(variantId) === -1) return;
      if (offerReason === 'entitled-mixed') {
        claimMixedVariant(variantId);
        return;
      }
      var err = root.querySelector('[data-club-gift-error="size"]');
      err.hidden = true;
      busy = true;
      root.querySelectorAll('[data-club-gift-variant]').forEach(function (b) { b.disabled = true; });
      addGift(variantId)
        .then(function (response) {
          // A size that sells out between opening the dialog and choosing it
          // comes back as a cart error rather than a rejected promise, and
          // closing quietly would read as a gift that was accepted.
          if (response && response.status && Number(response.status) >= 400) {
            throw new Error('unavailable');
          }
          close();
        })
        .catch(function () {
          err.hidden = false;
          // The shop just refused this size: re-ask the shelf and act on the
          // answer (an emptied shelf makes the floor hers). Queued: `busy`
          // is still held here, and its release drains the queue.
          refreshGiftAvailability().then(function () {
            syncQueuedWork = mergeSyncWork(syncQueuedWork, 'cart-change');
            drainQueuedSync();
          });
        })
        .finally(function () {
          releaseBusy();
          root.querySelectorAll('[data-club-gift-variant]').forEach(function (b) {
            b.disabled = b.hasAttribute('aria-disabled');
          });
        });
    });
  });


  // The strips are re-rendered by the cart, so the click is delegated. A
  // direct listener would survive exactly one cart update.
  document.addEventListener('click', function (e) {
    var strip = e.target.closest && e.target.closest('[data-club-gift-banner]');
    if (strip && !strip.hidden && (strip.dataset.state === 'offer' || strip.dataset.state === 'floor-offer')) {
      // She found the offer herself, so the introduction has been made.
      markPrompted();
      open('member');
    }
  });

  // The drawer fetches its own contents the first time it opens. That is not a
  // cart change and publishes nothing, so a banner arriving with it would be
  // rendered hidden and stay hidden. Repaint whenever that markup is swapped
  // in. childList only - toggling `hidden` is an attribute write and cannot
  // feed back into this.
  var drawer = document.querySelector('mini-cart');
  if (drawer && typeof MutationObserver === 'function') {
    new MutationObserver(function (records) {
      // Our own number landing in the strip is not a content swap to react
      // to; reacting to it is the loop described at paintFloorPercent.
      var own = Array.isArray(records) && records.length > 0 && records.every(function (record) {
        var target = record && record.target;
        return !!(target && target.closest && target.closest('[data-club-gift-floor-percent]'));
      });
      if (own) return;
      paintBanner();
      // Quick Add and gift wrapping replace these contents without announcing
      // a cart change, so the replacement itself outdates any proof an
      // in-flight repaint still holds. The reconcile queued right here runs
      // with a fresh read and owns whatever painting is still owed.
      cartGeneration += 1;
      // Painting alone would repeat the 5.8 failure in the drawer: the strip
      // would announce a gift from state read before the contents arrived.
      // The single-flight coalesces the repeats a content swap produces.
      requestReconcile('drawer');
    }).observe(drawer, { childList: true, subtree: true });
  }
  // The add itself is earlier than the paint it triggers; hearing it closes
  // the gap between the mutation landing and the drawer swap arriving.
  document.addEventListener('ajaxProduct:added', function () { cartGeneration += 1; });

  // Three quarters of cart traffic here is the in-app browser, where leaving
  // for the feed and coming back to a suspended tab is the ordinary path. The
  // page that returns carries stale prices, so the marked line is verified
  // again from the server rather than trusted from the DOM.
  var checkoutCoordinator = null;
  var mixedLifecycle = createMixedLifecycle({
    enabled: LIFECYCLE_ARMED,
    windowTarget: window,
    documentTarget: document,
    run: function (reason) {
      // A restored or refreshed page is a new attempt at checkout, so the
      // gate closes again and must be re-earned.
      if (checkoutCoordinator) checkoutCoordinator.reset();
      // One watch at a time: on every turn the earliest remembered clock
      // still ahead takes the watch. A clock that fired visible hands over
      // here with reason 'expiry'; one that fired hidden did no work and
      // hands over on the resume, so the handover cannot depend on the reason.
      var remaining = nextExpiryAfter(rememberedExpiries(), Date.now());
      if (remaining) mixedLifecycle.watchExpiry(remaining);
      // The scheduler owns restore refresh and cart reconciliation as one work
      // envelope; the lifecycle only delegates the event into that owner.
      return sync(reason);
    },
  });
  mixedLifecycle.start();
  function rememberedExpiries() {
    var recalledFloor = recalledFloorGrant();
    return [recalledMixedExpiry(), recalledFloor ? recalledFloor.expiresAt : null];
  }
  // A previous page view may have left a live grant behind on either lane;
  // watch the sooner expiry from the first moment rather than only after the
  // next claim. A clock already past still fires, so stale state is cleaned.
  var remembered = rememberedExpiries();
  mixedLifecycle.watchExpiry(earliestExpiry(remembered[0], remembered[1]));

  /*
   * The shelf is the one fact this page cannot keep for itself: another
   * shopper can take the last one while this tab sits in the background. The
   * lifecycle exists only while a code lane is armed, so the refresh is
   * bound separately and runs on every other road.
   */
  if (!LIFECYCLE_ARMED) {
    // Only when the lifecycle above is not running: it owns restores while the
    // bridge is armed, and two sets of handlers would race for the same job.
    window.addEventListener('pageshow', refreshOnRestore);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') refreshOnRestore();
    });
  }

  function refreshOnRestore() {
    refreshGiftAvailability().then(function () { requestReconcile('restore'); });
  }

  // The base theme announces cart changes two ways, and which one fires
  // depends on where the change came from. Both are its own published
  // contract - neither is a private detail, and nothing is wrapped. Both now
  // enter through the same single-flight as the restore surfaces, so a cart
  // change arriving mid-reconcile cannot start a second one.
  function requestReconcile(reason) {
    // Every revalidation starts from not knowing: a clean verdict from an
    // earlier read does not keep the controls visible while this read is
    // pending, or forever if it fails. The gate closes again too, whether or
    // not the lifecycle (which also closes it) is armed: a release earned on
    // an earlier cart is not a release for this one.
    if (checkoutCoordinator) checkoutCoordinator.reset();
    acceleratedCartState = 'unknown';
    hideAcceleratedCheckout();
    if (LIFECYCLE_ARMED) return mixedLifecycle.request(reason);
    return sync(reason);
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () { cartGeneration += 1; requestReconcile('cart-change'); });
  }
  document.addEventListener('cart:updated', function () { cartGeneration += 1; requestReconcile('cart-change'); });

  // The gate listens for the ordinary checkout button; the accelerated ones
  // (Shop Pay and friends) post the cart on their own. While the gate is
  // armed they are hidden, so every way into checkout passes the
  // verification. Repeated on every reconciliation: the footer that renders
  // them is re-rendered with the cart. Both the attribute and the inline
  // style, since a theme rule can outrank the attribute.
  // With the switch off, whether a code from before sits in the cart is
  // unknown until a read answers: hidden until one proves the cart clean,
  // kept hidden by one that lists a code.
  var acceleratedCartState = 'unknown';
  function hideAcceleratedCheckout(cart) {
    if (!GATE_ARMED) return;
    if (cart) acceleratedCartState = floorCodesIn(cart).length > 0 ? 'code' : 'clear';
    // A live benefit hides them for a shopper who can hold one; for a
    // guest only an unproven or code-carrying cart does.
    var hide = (GATE_LIVE && IS_LOGGED_IN) || acceleratedCartState !== 'clear';
    document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons').forEach(function (el) {
      if (hide) {
        el.hidden = true;
        if (el.style) el.style.display = 'none';
        if (el.dataset) el.dataset.clubGiftHidden = 'true';
        return;
      }
      // Revealed only what this gate hid; the footer's own state is its own.
      if (!el.dataset || el.dataset.clubGiftHidden !== 'true') return;
      el.hidden = false;
      if (el.style) el.style.display = '';
      delete el.dataset.clubGiftHidden;
    });
  }
  hideAcceleratedCheckout();

  // The last gate before money moves. It blocks the ordinary checkout button
  // first and only releases it once a fresh read has proved the marked line
  // is free - or, if it had to come off, once she has chosen to continue.
  checkoutCoordinator = createCheckoutCoordinator({
    enabled: GATE_ARMED,
    lockOnFailure: MIXED_ARMED,
    ensure: function () {
      if (mixedLockedOut) return Promise.resolve({ outcome: 'blocked', checkoutLocked: true });
      // A cart that cannot be read holds the press on either lane: a code
      // nobody verified must not ride to checkout on a failed read. The
      // mixed lane also locks (its line is money); the floor alone does not,
      // and the next press is judged from a read that answers.
      var unreadable = { outcome: 'blocked', checkoutLocked: MIXED_ARMED };
      return readCart().then(function (cart) {
        if (!cart) return unreadable;
        // A code list the runtime cannot read faithfully is no proof that no
        // floor code is retained: an empty parse of it holds the press.
        if (!cartCodesReadable(cart)) return unreadable;
        // Both lanes are judged before the form is released. The floor goes
        // first and may write; the mixed gate then judges a fresh cart, so a
        // marked line that turned charged meanwhile is still caught.
        var codesNow = floorCodesIn(cart).length > 0;
        // The floor lane (the Worker asked) runs for a logged-in shopper with
        // the switch on; a guest, or a switched-off floor, only retires.
        var floorRan = FLOOR_ENABLED && IS_LOGGED_IN && codesNow;
        var floorVerdict = floorRan ? ensureFloorAtCheckout(cart)
          : codesNow ? retireFloorCodes(cart)
            : Promise.resolve({ outcome: 'not_applicable' });
        return floorVerdict.then(function (verdict) {
          if (verdict.outcome !== 'not_applicable') return verdict;
          // Always a fresh read, whether or not the floor lane ran: a code
          // another tab applies right after the first read must be seen by
          // the exclusion below, not released beside a gift on a snapshot
          // formed before it arrived.
          var current = readCart();
          // After the floor lane, the other lane judges: the mixed verdict
          // when the bridge is armed, otherwise the native line's price.
          // The code the floor lane stood behind must still be the one
          // honoured code on the final snapshot; a lane that took the code
          // off must find none. Anything else is another tab's doing, and
          // the form is held so she does not reach a checkout showing the
          // full total under a strip that says the gift is on.
          var floorStillStands = function (v, fresh) {
            var codes = floorCodesIn(fresh);
            if (v.kept) {
              return codes.length === 1
                && String(codes[0]).toLowerCase() === String(v.kept).toLowerCase()
                && floorApplied(fresh, v.kept);
            }
            return codes.length === 0;
          };
          // The native line is judged by the native rules, not by its price
          // alone: a free line a stale tab added for a customer who has since
          // redeemed is one the rules remove. One repair is allowed, and the
          // repaired snapshot is judged by the same rules again; a cart that
          // still needs work after that holds the press (no lockout).
          // The press is held; whether the gate also locks is the lane's call.
          var held = function () { return { outcome: 'blocked', checkoutLocked: MIXED_ARMED }; };
          var judgeNative = function (c, repaired) {
            var measured = measureCart(c, VARIANTS);
            var decision = decideGift(measured, {
              thresholdMinor: THRESHOLD,
              isMember: IS_MEMBER,
              isLoggedIn: IS_LOGGED_IN,
              hasRedeemed: HAS_REDEEMED,
              spendMinor: SPEND_MINOR,
              giftAvailable: giftAvailableNow(),
              floorEnabled: FLOOR_ENABLED,
            });
            if (decision.action === 'fix-quantity') {
              if (repaired) return held();
              return setGiftQuantity(decision.variant, 1)
                .then(function () { return readCart(); })
                .then(function (after) {
                  if (!after) return held();
                  // The repaired snapshot goes back through the whole
                  // judgment, floor exclusion included: a code another tab
                  // applied while the repair was in flight is a second
                  // benefit beside the line just repaired.
                  return judgeFinal(after, true);
                })
                .catch(held);
            }
            if (!measured.giftCharged && decision.action !== 'remove-gift') return { outcome: 'not_applicable' };
            // A native gift the rules reject, or a charged one, is cleaned,
            // not latched: the line comes off, a fresh read must agree, and
            // she is told it came off and decides whether to continue. No
            // lockout: the next press is judged afresh.
            return removeAllGiftLines()
              .then(function () { return readCart(); })
              .then(function (after) {
                if (!after || measureCart(after, VARIANTS).giftLines > 0) return { outcome: 'removed_unverified' };
                return { outcome: 'removed_ineligible' };
              })
              .catch(function () { return { outcome: 'removed_unverified' }; });
          };
          var judgeRest = function (c, repaired) {
            if (MIXED_ARMED) return reconcileMixed(c, 'checkout', 0);
            if (!CAMPAIGN_ON) return { outcome: 'not_applicable' };
            return judgeNative(c, repaired);
          };
          // Every snapshot that could release the form passes here: the
          // first one and the one after a repair alike.
          var judgeFinal = function (fresh, repaired) {
            if (!cartCodesReadable(fresh)) return held();
            if (floorRan && !floorStillStands(verdict, fresh)) return held();
            if (!floorBesideGift(fresh)) {
              // A code the floor lane never saw (it was not there on the
              // first read) does not ride to checkout unverified: the press
              // is held, and the next one runs the lane on it.
              if (!floorRan && floorCodesIn(fresh).length > 0) return held();
              return judgeRest(fresh, repaired);
            }
            // Both benefits in one cart: the floor code leaves, and only a
            // read that no longer lists it lets the other lane judge. A code
            // list that cannot be read faithfully is never rewritten; the
            // press is held instead of releasing both.
            var besideVerdict = floorCodeVerdict(fresh, { action: 'none', reason: 'gift-present' }, true);
            if (besideVerdict.action !== 'remove') return held();
            return applyMixedDiscountCodes(besideVerdict.remaining)
              .then(function () { return readCart(); })
              .then(function (after) {
                if (!after || floorCodesIn(after).length > 0) return held();
                return judgeRest(after, repaired);
              })
              .catch(held);
          };
          return current.then(function (fresh) {
            if (!fresh) return unreadable;
            return judgeFinal(fresh, false);
          }, function () { return unreadable; });
        });
      }, function () { return unreadable; });
    },
    submit: function (context) {
      if (!context || !context.form) return;
      var form = context.form;
      var button = context.button;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(button && button.form === form ? button : undefined);
        return;
      }
      // Older engines lose the submitter entirely, so its name/value is
      // carried by a field instead. Without it the cart posts as an update.
      if (button && button.name) {
        var carrier = document.createElement('input');
        carrier.type = 'hidden';
        carrier.name = button.name;
        carrier.value = button.value || '';
        form.appendChild(carrier);
      }
      form.submit();
    },
    notify: function (state, result) {
      var locked = !!(result && result.checkoutLocked);
      // A floor code taken off at the gate has its own words: the discount
      // is gone, nothing of hers was removed, and the full total is next.
      if (result && result.outcome === 'removed_floor') { setBanner('floor-removed'); return; }
      // A removal is reported as one. A held press on the floor alone took
      // nothing off that she could be told about, so the strip says the cart
      // could not be verified. The mixed lane's block keeps the removal
      // wording: its line is money, and what could not be confirmed there is
      // the cleanup of that line.
      setBanner(state === 'removed' || locked ? 'error' : 'held');
      // The lock is the mixed lane's: its line is money and the reconciler
      // that clears it runs only while the bridge is armed. A floor-only
      // failure holds this press and leaves the next one to be judged afresh.
      if (state === 'error' && locked) mixedLockedOut = true;
    },
  });

  /*
   * The floor's last gate. The Worker is asked for checkout runway; a code it
   * will no longer stand behind - a size came back, the lease is gone - is
   * taken out of the cart so the checkout she reaches shows no dead code.
   * A code the Worker stands behind never holds her: the percentage is a
   * gift, not a condition. A refused code holds her only until a fresh read
   * proves it gone; a cleanup that cannot be confirmed keeps the form, and
   * the strip says why, rather than sending an unverified code to checkout.
   */
  function ensureFloorAtCheckout(cart) {
    return requestFloorGrant('checkout')
      .then(function (answer) {
        var granted = checkedFloorGrant(answer.body);
        // The answer is judged against the cart as it is now, not the
        // snapshot taken before the request: another tab may have changed
        // the codes while the Worker was asked.
        return readCart().then(function (fresh) {
          if (!fresh) throw new Error('cart-unavailable');
          return { granted: granted, cart: fresh };
        });
      })
      .then(function (now) {
        var granted = now.granted;
        var current = now.cart;
        // Released only with runway: payment takes longer than a code that
        // dies in seconds, and the total she would see there is the full
        // one. The Worker renews the lease at the gate, so a healthy answer
        // always clears this; one that does not is treated as refused.
        var runway = granted && !mixedGrantExpired(granted.expiresAt, Date.now(), MIXED_CHECKOUT_MARGIN_MS);
        var onlyOne = floorCodesIn(current).length === 1;
        if (granted && runway) {
          if (onlyOne && floorApplied(current, granted.code)) {
            rememberFloorGrant(granted.expiresAt, granted.percent);
            return { outcome: 'not_applicable', kept: granted.code };
          }
          // The Worker stands behind a code other than the one the cart
          // carries (a replacement after an expiry, a recovery): it is
          // applied in the stale one's place, her other codes intact, and
          // a read must show it honoured before release. A list that cannot
          // be read faithfully is never rewritten.
          if (!cartCodesReadable(current)) return { outcome: 'blocked', checkoutLocked: MIXED_ARMED };
          var others = cartExplicitCodes(current).filter(function (code) { return !FLOOR_CODE_RE.test(code); });
          // Remembered only once a read proves the replacement applied: a
          // clock recorded ahead of that would read the old, merely
          // still-listed code as live with the replacement's percentage.
          return applyMixedDiscountCodes(mergeDiscountCodes(others, granted.code))
            .then(function () { return readCart(); })
            .then(function (fresh) {
              if (fresh && floorCodesIn(fresh).length === 1 && floorApplied(fresh, granted.code)) {
                rememberFloorGrant(granted.expiresAt, granted.percent);
                return { outcome: 'not_applicable', kept: granted.code };
              }
              return { outcome: 'blocked', checkoutLocked: MIXED_ARMED };
            });
        }
        var verdict = floorCodeVerdict(current, { action: 'none', reason: 'checkout-refused' }, true);
        if (verdict.action !== 'remove') return { outcome: 'not_applicable' };
        offerReason = null;
        setBanner(null);
        // The plain mutation here on purpose: she is on her way to checkout,
        // and a cart-page reload would take that press away from her. The
        // removal is a verdict, not a release: she is told the discount came
        // off and the next press, at the full total, is hers to make.
        return applyMixedDiscountCodes(verdict.remaining)
          .then(function () { return readCart(); })
          .then(function (fresh) {
            if (fresh && floorCodesIn(fresh).length === 0) return { outcome: 'removed_floor' };
            return { outcome: 'blocked', checkoutLocked: MIXED_ARMED };
          });
      })
      .catch(function () { return { outcome: 'blocked', checkoutLocked: MIXED_ARMED }; });
  }

  /*
   * The switch is off and a code from before is still in the cart: it is
   * retired here, her other codes rewritten intact, and only a read that no
   * longer lists it releases the form. The Worker is not asked; there is no
   * benefit to renew. A list that cannot be read faithfully holds the press.
   */
  function retireFloorCodes(cart) {
    var verdict = floorCodeVerdict(cart, { action: 'none', reason: 'floor-off' }, true);
    if (verdict.action !== 'remove') return Promise.resolve({ outcome: 'blocked', checkoutLocked: MIXED_ARMED });
    return applyMixedDiscountCodes(verdict.remaining)
      .then(function () { return readCart(); })
      .then(function (fresh) {
        // A verdict, not a release: the next press is hers to make.
        if (fresh && floorCodesIn(fresh).length === 0) return { outcome: 'removed_floor' };
        return { outcome: 'blocked', checkoutLocked: MIXED_ARMED };
      })
      .catch(function () { return { outcome: 'blocked', checkoutLocked: MIXED_ARMED }; });
  }

  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('[name="checkout"]');
    if (!button) return;
    var form = button.form || (button.closest && button.closest('form'));
    checkoutCoordinator.handle(e, { form: form, button: button });
  }, true);

  // On load the cart is measured, and whether the dialog may interrupt is
  // decidePrompt's call: never for the cart road, once ever for the accrual
  // road, always for a customer coming back from the join journey she
  // started here.
  sync(claimReturnMark() ? 'return' : 'landing');
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}

// Exported for the offline rule tests. There is one copy of these rules, and
// this is it - the tests read this same file rather than a transcription.
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    measureCart: measureCart,
    decideGift: decideGift,
    decidePrompt: decidePrompt,
    offerLockout: offerLockout,
    measureMixedMarker: measureMixedMarker,
    decideMixedGift: decideMixedGift,
    mergeDiscountCodes: mergeDiscountCodes,
    reconcileMixedGift: reconcileMixedGift,
    keptReason: keptReason,
    earliestExpiry: earliestExpiry,
    nextExpiryAfter: nextExpiryAfter,
    syncWorkFor: syncWorkFor,
    mergeSyncWork: mergeSyncWork,
    triggerForSyncWork: triggerForSyncWork,
    createMixedLifecycle: createMixedLifecycle,
    createCheckoutCoordinator: createCheckoutCoordinator,
    safeMixedProxyEndpoint: safeMixedProxyEndpoint,
    mixedDrawerNeedsRepaint: mixedDrawerNeedsRepaint,
    renderMixedCart: renderMixedCart,
    checkedFloorGrant: checkedFloorGrant,
    floorApplied: floorApplied,
    floorCodeVerdict: floorCodeVerdict,
    floorCodesIn: floorCodesIn,
  };
}
